import React, { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Form, Table, Card } from 'react-bootstrap';
import Layout from "../../utils/layout";
import useStore from "../../hooks/useStore";
import { observer } from "mobx-react";
import ScorecardOverview from "./ScorecardOverview";
import YTDChart from "./YTDChart";
import { computeThemeSummary } from "../../utils/helpers";
import * as XLSX from "xlsx";
import { toPng } from 'html-to-image';
import ScorecardKPIChart from "./ScorecardKPIChart";
import { 
  calculateGroupedTarget, 
  handleDownloadExcel, 
  handleDownloadChartAndTableImage,
  calculateFieldTargets,
  fetchScorecardAssignedFields,
  fetchAllScorecardData,
  fetchGovernanceData,
  fetchSocialData,
  aggregateYearlyData
} from "../../utils/ScorecardUtils";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

const ScorecardExcel = () => {
  const {
    companyStore: { companyUnit, assignUnitsRaw, fetchAssignUnits, getCompanyUnitName },
    userStore: { user, token },
    dashboardStore 
  } = useStore();

  const [allUnitsDetailedData, setAllUnitsDetailedData] = useState([]);

  const [frequency, setFrequency] = useState("Yearly");
  const [year, setYear] = useState(currentYear.toString());
  const [month, setMonth] = useState("January");
  const [ytdFromMonth, setYtdFromMonth] = useState("January");
  const [ytdToMonth, setYtdToMonth] = useState("December"); 

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const [scorecardData, setScorecardData] = useState({ water: [], waste: [], emission: [], energy: [] });
  const [loading, setLoading] = useState(false);
  const [targetValues, setTargetValues] = useState({}); 
const [statusSelections, setStatusSelections] = useState({});

 const [groupedTargetValues, setGroupedTargetValues] = useState({ water: {}, waste: {} });
 const [governanceData, setGovernanceData] = useState([]);

 const [socialData, setSocialData] = useState([]);

 const [unitWiseSummary, setUnitWiseSummary] = useState([]);

 const [overallThemeSummary, setOverallThemeSummary] = useState({
  Environment: { total: 0, reached: 0 },
  Governance: { total: 0, reached: 0 },
  Social: { total: 0, reached: 0 },
  Overall: { total: 0, reached: 0 },
  percentages: {
    Environment: "0.00",
    Governance: "0.00",
    Social: "0.00",
    Overall: "0.00"
  }
});

const [themeCountsState, setThemeCountsState] = useState({
  Environment: { total: 0, reached: 0 },
  Governance: { total: 0, reached: 0 },
  Social: { total: 0, reached: 0 }
});

const [showAllUnits, setShowAllUnits] = useState(false);

// Helper function to get current month index for YTD calculations
const getCurrentMonthIndex = () => {
  const now = new Date();
  return now.getMonth(); // 0-based index (0 = January, 11 = December)
};

// Helper function to check if a date/month is within YTD range
const isWithinYTD = (itemYear, itemMonth, targetYear) => {
  if (itemYear !== targetYear) return false;
  const itemMonthIndex = months.indexOf(itemMonth);
  const fromMonthIndex = months.indexOf(ytdFromMonth);
  const toMonthIndex = months.indexOf(ytdToMonth);
  return itemMonthIndex >= fromMonthIndex && itemMonthIndex <= toMonthIndex;
};

const calculateGroupedWaterTarget = () => {
  if (frequency === "YTD") {
    const fromMonthIndex = months.indexOf(ytdFromMonth);
    const toMonthIndex = months.indexOf(ytdToMonth);
    const monthsInRange = toMonthIndex - fromMonthIndex + 1;
    return calculateGroupedTarget(groupedTargetValues, 'water', 'Monthly') * monthsInRange;
  }
  return calculateGroupedTarget(groupedTargetValues, 'water', frequency);
};

const calculateGroupedWasteTarget = () => {
  if (frequency === "YTD") {
    const fromMonthIndex = months.indexOf(ytdFromMonth);
    const toMonthIndex = months.indexOf(ytdToMonth);
    const monthsInRange = toMonthIndex - fromMonthIndex + 1;
    return calculateGroupedTarget(groupedTargetValues, 'waste', 'Monthly') * monthsInRange;
  }
  return calculateGroupedTarget(groupedTargetValues, 'waste', frequency);
};


  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || !companyUnit) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const assignedFields = await fetchScorecardAssignedFields(user.id, token, companyUnit, year, setTargetValues, setGroupedTargetValues, setStatusSelections);

        if (assignedFields.length > 0) {
          const fieldIds = assignedFields.map(item => item.fieldId);
          await fetchAllScorecardData(fieldIds, companyUnit, user.id, year, token, setScorecardData);

          const kpiIds = [...new Set(assignedFields.map(f => f.kpiId))];
          const catIds = [...new Set(assignedFields.map(f => f.catId))];
          const subCatIds = [...new Set(assignedFields.map(f => f.subCatId))];
          const gfieldIds = [...new Set(assignedFields.map(f => f.fieldId))];
          const companyIds = [...new Set(assignedFields.map(f => f.companyId))];
          
          const governanceResults = await fetchGovernanceData(
            kpiIds,
            catIds,
            subCatIds,
            gfieldIds,
            companyIds,
            [companyUnit], 
            [year],
            token
          );
        
          setGovernanceData(governanceResults);

          const socialResults = await fetchSocialData(
            kpiIds,
            catIds,
            subCatIds,
            gfieldIds,
            companyIds,
            [companyUnit],
            year,
            token
          );
          setSocialData(socialResults);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, companyUnit, year, token]);


const themeCounts = {
  Environment: { total: 0, reached: 0 },
  Governance: { total: 0, reached: 0 },
  Social: { total: 0, reached: 0 },
};

const fetchAllUnitData = async () => {
  if (!user?.id || !token) return;
  setLoading(true);

  try {
    const unitRes = await fetch(`${process.env.REACT_APP_BASE_URL}assign-unit/getAll`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const unitResult = await unitRes.json();
    const filteredUnits = unitResult.data.filter((unit) => unit.companyId === user?.id);
    if (!unitResult.status) return;

    const units = filteredUnits;
    const summaryList = [];
    const detailedDataList = [];

    for (const unit of units) {
      const unitId = String(unit.id);
      const unitName = unit.name || unitId;

      // console.log(`\n=== Processing Unit: ${unitName} (ID: ${unitId}) ===`);

      // Fetch assigned fields
      const fieldsRes = await fetch(`${process.env.REACT_APP_BASE_URL}assign-companies/assignFields/scorecard/${user.id}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      const fieldsResult = await fieldsRes.json();
      if (!fieldsResult.status) continue;

      const assignedFields = fieldsResult.data;
      const fieldIds = assignedFields.map(f => String(f.fieldId));

      // Fetch base year data
      const baseYear = String(Number(year) - 1);
      const baseDataResponse = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fieldIds, companyUnit: unitId, userId: String(user.id), selectedYear: baseYear }),
      });
      const baseYearResult = await baseDataResponse.json();
      const baseYearData = baseYearResult.data || {};

      // Fetch base year governance data
      const govBaseRes = await fetch(`${process.env.REACT_APP_BASE_URL}governance/getGovernanceData`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          kpiId: assignedFields.map(f => f.kpiId), catId: assignedFields.map(f => f.catId),
          subCatId: assignedFields.map(f => f.subCatId), fieldId: assignedFields.map(f => f.fieldId),
          companyId: assignedFields.map(f => f.companyId), companyUnit: [unitId], year: [baseYear],
        }),
      });
      baseYearData.governance = (await govBaseRes.json()).data || [];

      // Fetch base year social data
      const socBaseRes = await fetch(`${process.env.REACT_APP_BASE_URL}social/getSocialData`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          kpiId: assignedFields.map(f => f.kpiId), catId: assignedFields.map(f => f.catId),
          subCatId: assignedFields.map(f => f.subCatId), fieldId: assignedFields.map(f => f.fieldId),
          companyId: assignedFields.map(f => f.companyId), companyUnit: [unitId], year: [baseYear],
        }),
      });
      baseYearData.social = (await socBaseRes.json()).data || [];

      // Calculate targets
      const { targets: unitTargetMap, fieldStatusMapping: unitStatusMap } = calculateFieldTargets(assignedFields, baseYearData, year);

      // Fetch current year scorecard data
      const scoreRes = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fieldIds, companyUnit: unitId, userId: String(user.id), selectedYear: String(year) }),
      });

      const scoreResult = await scoreRes.json();
      const scorecardData = scoreResult.data || { water: [], waste: [], emission: [], energy: [] };

      // Process grouped data with daily support
      const groupedData = { water: {}, waste: {} };

      (scorecardData.water || []).forEach(item => {
        if (item.fieldGroup === "A") {
          let key;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            key = `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`;
          } else if (item.frequencyType === "Monthly" && item.frequencyMonth && item.frequencyYear) {
            key = `${item.frequencyMonth}-${item.frequencyYear}`;
          } else if (item.frequencyType === "Yearly" && item.frequencyYear) {
            // Explicit yearly grouped water
            key = `Yearly-${item.frequencyYear}`;
          }
          if (key) groupedData.water[key] = (groupedData.water[key] || 0) + (item.prevValue || 0);
        }
      });

      (scorecardData.waste || []).forEach(item => {
        if (["B1", "B2", "B3"].includes(item.fieldGroup)) {
          let key;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            key = `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`;
          } else if (item.frequencyType === "Monthly" && item.frequencyMonth && item.frequencyYear) {
            key = `${item.frequencyMonth}-${item.frequencyYear}`;
          }
          if (key) groupedData.waste[key] = (groupedData.waste[key] || 0) + (item.prevValue || 0);
        }
      });

      const unitGroupedTargetValues = {
        water: { total: (baseYearData.water || []).filter(item => item.fieldGroup === "A" && item.prevValue !== null && !isNaN(item.prevValue)).reduce((sum, item) => sum + Number(item.prevValue), 0) * 0.98 },
        waste: { total: (baseYearData.waste || []).filter(item => ["B1", "B2", "B3"].includes(item.fieldGroup) && item.prevValue !== null && !isNaN(item.prevValue)).reduce((sum, item) => sum + Number(item.prevValue), 0) * 1.02 },
      };

      // Fetch current year governance and social data
      const sharedFilter = {
        kpiId: assignedFields.map(f => f.kpiId), catId: assignedFields.map(f => f.catId),
        subCatId: assignedFields.map(f => f.subCatId), fieldId: assignedFields.map(f => f.fieldId),
        companyId: assignedFields.map(f => f.companyId), companyUnit: [unitId], year: [Number(year)],
      };

      const govRes = await fetch(`${process.env.REACT_APP_BASE_URL}governance/getGovernanceData`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(sharedFilter),
      });
      const govResult = await govRes.json();

      const socRes = await fetch(`${process.env.REACT_APP_BASE_URL}social/getSocialData`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(sharedFilter),
      });
      const socResult = await socRes.json();

      // Store detailed data
      detailedDataList.push({
        unitName, unitId,
        scorecardData: { ...scorecardData, groupedWater: groupedData.water, groupedWaste: groupedData.waste },
        governanceData: govResult.data || [], socialData: socResult.data || [],
        targetValues: unitTargetMap, statusSelections: unitStatusMap, groupedTargetValues: unitGroupedTargetValues,
      });

      // **COMPLETE THEME CALCULATION** - This was missing!
//       const filteredScorecardData = ['water', 'waste', 'emission', 'energy'].reduce((acc, type) => {
//         const items = scorecardData[type] || [];
//         if (frequency === "Monthly" && month !== "All") {
//           acc[type] = items.filter(i => {
//             if (i.frequencyType === "Daily" && i.frequencyDate) {
//               const monthName = new Date(i.frequencyDate).toLocaleString('default', { month: 'long' });
//               return monthName === month;
//             } else {
//               return i.frequencyMonth === month;
//             }
//           });
//         } else {
//           acc[type] = aggregateYearlyData(items, year);
//         }
//         return acc;
//       }, {});

      
// const themeCounts = { 
//   Environment: { total: 0, reached: 0 }, 
//   Governance: { total: 0, reached: 0 }, 
//   Social: { total: 0, reached: 0 } 
// };

// // Environment theme calculation
// ['water', 'waste', 'emission', 'energy'].forEach(dataset => {
//   const datasetItems = filteredScorecardData[dataset] || [];

//   // Handle grouped water
//   if (dataset === "water") {
//     const groupAItems = datasetItems.filter(i => i.fieldGroup === "A");
    
//     if (groupAItems.length > 0) {
//       const total = groupAItems.reduce((sum, i) => sum + Number(i.prevValue || 0), 0);
//       const targets = groupAItems.map(i => unitTargetMap[i.fieldId]).filter(Boolean);
//       const targetSum = targets.reduce((a, b) => a + b, 0);
      
//       themeCounts.Environment.total++;
//       if (total <= targetSum) {
//         themeCounts.Environment.reached++;
//       }
//     }
//   }
  
//   // Handle grouped waste
//   if (dataset === "waste") {
//     const groupBItems = datasetItems.filter(i => ["B1", "B2", "B3"].includes(i.fieldGroup));
//     if (groupBItems.length > 0) {
//       const total = groupBItems.reduce((sum, i) => sum + Number(i.prevValue || 0), 0);
//       const targets = groupBItems.map(i => unitTargetMap[i.fieldId]).filter(Boolean);
//       const targetSum = targets.reduce((a, b) => a + b, 0);
  
//       themeCounts.Environment.total++;
//       if (total >= targetSum) {
//         themeCounts.Environment.reached++;
//       }
//     }
//   }

//   // Handle individual fields (non-grouped)
//   datasetItems.forEach(item => {
//     if ((dataset === "water" && item.fieldGroup === "A") ||
//         (dataset === "waste" && ["B1", "B2", "B3"].includes(item.fieldGroup))) return;

//     const selected = unitStatusMap[item.fieldId] === "isLeading" ? "leading"
//       : unitStatusMap[item.fieldId] === "isLagging" ? "lagging" : "-";
//     const current = Number(item.prevValue || 0);
//     const target = Number(unitTargetMap[item.fieldId]);

//     if (selected === "-" || isNaN(target)) return;

//     // Calculate proper target based on frequency
//     let targetValue;
//     if (frequency === "Yearly") {
//       targetValue = target;
//     } else {
//       // For monthly, use monthly target (yearly target / 12)
//       targetValue = target / 12;
//     }

//     themeCounts.Environment.total++;
//     if ((selected === "leading" && current >= targetValue) ||
//       (selected === "lagging" && current < targetValue)) {
//       themeCounts.Environment.reached++;
//     }
//   });
// });


// **FIXED THEME CALCULATION for Show All Units**
const filteredScorecardData = {};

// Process each environment dataset
['water', 'waste', 'emission', 'energy'].forEach(type => {
  const items = scorecardData[type] || [];
  
  if (frequency === "Monthly") {
    if (month !== "All") {
      // Specific month selected - filter for that month only
      filteredScorecardData[type] = items.filter(i => {
        if (i.frequencyType === "Daily" && i.frequencyDate) {
          const monthName = new Date(i.frequencyDate).toLocaleString('default', { month: 'long' });
          return monthName === month;
        } else {
          return i.frequencyMonth === month;
        }
      });
    } else {
      // "All" months selected - aggregate by fieldId only (not by month)
      const aggregatedByField = {};
      // For grouped water, check for explicit yearly value
      if (type === 'water') {
        // Find explicit yearly grouped water for this year
        const explicitYearly = items.find(item => item.fieldGroup === 'A' && item.frequencyType === 'Yearly' && item.frequencyYear === year);
        if (explicitYearly) {
          // Only use explicit yearly value for grouped water
          aggregatedByField['groupedYearlyWater'] = {
            ...explicitYearly,
            prevValue: Number(explicitYearly.prevValue || 0),
            aggregatedMonths: new Set(['Yearly'])
          };
        } else {
          // No explicit yearly, sum up all months as before
          items.filter(item => {
            // Filter by year
            let itemYear;
            if (item.frequencyType === "Daily" && item.frequencyDate) {
              itemYear = new Date(item.frequencyDate).getFullYear().toString();
            } else if (item.frequencyYear) {
              itemYear = item.frequencyYear;
            } else {
              return false;
            }
            return itemYear === year;
          }).forEach(item => {
            const fieldId = item.fieldId;
            if (!aggregatedByField[fieldId]) {
              aggregatedByField[fieldId] = {
                ...item,
                prevValue: 0,
                aggregatedMonths: new Set()
              };
            }
            aggregatedByField[fieldId].prevValue += Number(item.prevValue || 0);
            let itemMonth;
            if (item.frequencyType === "Daily" && item.frequencyDate) {
              itemMonth = new Date(item.frequencyDate).toLocaleString('default', { month: 'long' });
            } else {
              itemMonth = item.frequencyMonth || 'Unknown';
            }
            aggregatedByField[fieldId].aggregatedMonths.add(itemMonth);
          });
        }
        filteredScorecardData[type] = Object.values(aggregatedByField);
      } else {
        // Not water, use original logic
        items.filter(item => {
          // Filter by year
          let itemYear;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            itemYear = new Date(item.frequencyDate).getFullYear().toString();
          } else if (item.frequencyYear) {
            itemYear = item.frequencyYear;
          } else {
            return false;
          }
          return itemYear === year;
        }).forEach(item => {
          const fieldId = item.fieldId;
          if (!aggregatedByField[fieldId]) {
            aggregatedByField[fieldId] = {
              ...item,
              prevValue: 0,
              aggregatedMonths: new Set()
            };
          }
          aggregatedByField[fieldId].prevValue += Number(item.prevValue || 0);
          let itemMonth;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            itemMonth = new Date(item.frequencyDate).toLocaleString('default', { month: 'long' });
          } else {
            itemMonth = item.frequencyMonth || 'Unknown';
          }
          aggregatedByField[fieldId].aggregatedMonths.add(itemMonth);
        });
        filteredScorecardData[type] = Object.values(aggregatedByField);
      }
    }
  } else if (frequency === "YTD") {
    // YTD frequency - aggregate by fieldId for current year up to current month
    const ytdAggregatedByField = {};
    
    items.filter(item => {
      let itemYear, itemMonth;
      if (item.frequencyType === "Daily" && item.frequencyDate) {
        const date = new Date(item.frequencyDate);
        itemYear = date.getFullYear().toString();
        itemMonth = date.toLocaleString('default', { month: 'long' });
      } else if (item.frequencyYear) {
        itemYear = item.frequencyYear;
        itemMonth = item.frequencyMonth || 'Unknown';
      } else {
        return false;
      }
      
      if (itemYear !== year) return false;
      return isWithinYTD(itemYear, itemMonth, year);
    }).forEach(item => {
      const fieldId = item.fieldId;
      
      if (!ytdAggregatedByField[fieldId]) {
        ytdAggregatedByField[fieldId] = {
          ...item,
          prevValue: 0,
          aggregatedMonths: new Set()
        };
      }
      
      ytdAggregatedByField[fieldId].prevValue += Number(item.prevValue || 0);
      
      // Track which month this data is from
      let itemMonth;
      if (item.frequencyType === "Daily" && item.frequencyDate) {
        itemMonth = new Date(item.frequencyDate).toLocaleString('default', { month: 'long' });
      } else {
        itemMonth = item.frequencyMonth || 'Unknown';
      }
      ytdAggregatedByField[fieldId].aggregatedMonths.add(itemMonth);
    });
    
    filteredScorecardData[type] = Object.values(ytdAggregatedByField);
  } else {
    // Yearly frequency - use existing aggregateYearlyData function
    filteredScorecardData[type] = aggregateYearlyData(items, year);
  }
});

const themeCounts = { 
  Environment: { total: 0, reached: 0 }, 
  Governance: { total: 0, reached: 0 }, 
  Social: { total: 0, reached: 0 } 
};

// console.log(`🔍 ALL UNITS - Processing unit: ${unitName}, Frequency: ${frequency}, Month: ${month}`);

// Environment theme calculation
['water', 'waste', 'emission', 'energy'].forEach(dataset => {
  const datasetItems = filteredScorecardData[dataset] || [];
  console.log(`📊 ${unitName} - ${dataset} dataset: ${datasetItems.length} items after filtering`);

  // Handle grouped water
  // if (dataset === "water") {
  //   const groupAItems = datasetItems.filter(i => i.fieldGroup === "A");
    
  //   if (groupAItems.length > 0) {
  //     const total = groupAItems.reduce((sum, i) => sum + Number(i.prevValue || 0), 0);
  //     const targets = groupAItems.map(i => unitTargetMap[i.fieldId]).filter(Boolean);
  //     const targetSum = targets.reduce((a, b) => a + b, 0);
      
  //     // console.log(`🌊 ${unitName} - Grouped water: total=${total}, targetSum=${targetSum}`);
      
  //     themeCounts.Environment.total++;
  //     if (total <= targetSum) {
  //       themeCounts.Environment.reached++;
  //     }
  //   }
  // }


  // Handle grouped water - FIXED TARGET CALCULATION
if (dataset === "water") {
  const groupAItems = datasetItems.filter(i => i.fieldGroup === "A");
  
  if (groupAItems.length > 0) {
    const total = groupAItems.reduce((sum, i) => sum + Number(i.prevValue || 0), 0);
    
    // FIXED: Use grouped target values instead of individual field targets
    let targetValue;
    const totalGroupedTarget = Object.values(unitGroupedTargetValues.water).reduce((sum, target) => sum + (target || 0), 0);
    
    if (frequency === "Yearly") {
      targetValue = totalGroupedTarget;
    } else if (frequency === "Monthly") {
      if (month === "All") {
        targetValue = totalGroupedTarget; // Full year target for all months
      } else {
        targetValue = totalGroupedTarget / 12; // Monthly target
      }
    } else if (frequency === "YTD") {
      const months = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
      const fromMonthIndex = months.indexOf(ytdFromMonth);
      const toMonthIndex = months.indexOf(ytdToMonth);
      const monthsInRange = toMonthIndex - fromMonthIndex + 1;
      targetValue = (totalGroupedTarget / 12) * monthsInRange; // YTD prorated target
    }
    
    console.log(`🌊 ${unitName} - Grouped water: total=${total}, targetValue=${targetValue}, totalGroupedTarget=${totalGroupedTarget}`);
    
    themeCounts.Environment.total++;
    if (total <= targetValue) {
      themeCounts.Environment.reached++;
      console.log(`✅ ${unitName} - Grouped water target REACHED`);
    } else {
      console.log(`❌ ${unitName} - Grouped water target NOT reached`);
    }
  }
}
  
  // Handle grouped waste
  if (dataset === "waste") {
    const groupBItems = datasetItems.filter(i => ["B1", "B2", "B3"].includes(i.fieldGroup));
    if (groupBItems.length > 0) {
      const total = groupBItems.reduce((sum, i) => sum + Number(i.prevValue || 0), 0);
      const targets = groupBItems.map(i => unitTargetMap[i.fieldId]).filter(Boolean);
      const targetSum = targets.reduce((a, b) => a + b, 0);
  
      console.log(`🗑️ ${unitName} - Grouped waste: total=${total}, targetSum=${targetSum}`);
      
      themeCounts.Environment.total++;
      if (total >= targetSum) {
        themeCounts.Environment.reached++;
      }
    }
  }

  // Handle individual fields (non-grouped) - KEY FIX HERE
  const processedFields = new Set(); // Track processed fieldIds to avoid duplicates
  
  datasetItems.forEach(item => {
    // Skip grouped items
    if ((dataset === "water" && item.fieldGroup === "A") ||
        (dataset === "waste" && ["B1", "B2", "B3"].includes(item.fieldGroup))) {
      return;
    }

    const fieldId = item.fieldId;
    
    // IMPORTANT: Skip if we've already processed this field
    if (processedFields.has(fieldId)) {
      console.log(`⏭️ ${unitName} - Skipping duplicate field ${fieldId}`);
      return;
    }
    processedFields.add(fieldId);

    const selected = unitStatusMap[item.fieldId] === "isLeading" ? "leading"
      : unitStatusMap[item.fieldId] === "isLagging" ? "lagging" : "-";
    const current = Number(item.prevValue || 0);
    const target = Number(unitTargetMap[item.fieldId]);

    if (selected === "-" || isNaN(target)) {
      console.log(`⏭️ ${unitName} - Field ${fieldId}: No status or target`);
      return;
    }

    // Calculate proper target based on frequency
    let targetValue;
    if (frequency === "Yearly") {
      targetValue = target;
    } else if (frequency === "YTD") {
      // For YTD, calculate pro-rated target based on selected month range
      const months = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
      const fromMonthIndex = months.indexOf(ytdFromMonth);
      const toMonthIndex = months.indexOf(ytdToMonth);
      const monthsInRange = toMonthIndex - fromMonthIndex + 1;
      targetValue = (target / 12) * monthsInRange;
    } else {
      // For monthly frequency
      if (month === "All") {
        // When "All" months, use full yearly target since we aggregated all months
        targetValue = target;
      } else {
        // When specific month, use monthly target
        targetValue = target / 12;
      }
    }

    const isTargetReached = (selected === "leading" && current >= targetValue) ||
                           (selected === "lagging" && current < targetValue);

    console.log(`🎯 ${unitName} - Field ${fieldId}: current=${current}, target=${targetValue}, reached=${isTargetReached}, months=${item.aggregatedMonths ? Array.from(item.aggregatedMonths).join(',') : 'single'}`);

    themeCounts.Environment.total++;
    if (isTargetReached) {
      themeCounts.Environment.reached++;
    }
  });
});

// console.log(`✅ ${unitName} - Environment theme: ${themeCounts.Environment.reached}/${themeCounts.Environment.total}`);

// Governance theme calculation (unchanged)
(govResult.data || []).forEach(item => {
  const val = Number(item.value || 0);
  const tgt = Number(unitTargetMap[item.fieldId]);
  const stat = unitStatusMap[item.fieldId];
  const selected = stat === "isLeading" ? "leading" : stat === "isLagging" ? "lagging" : "-";

  if (selected === "-" || isNaN(tgt)) return;

  themeCounts.Governance.total++;
  if ((selected === "leading" && val >= tgt) || (selected === "lagging" && val < tgt)) {
    themeCounts.Governance.reached++;
  }
});

// Social theme calculation (unchanged except for gender diversity)
(socResult.data || []).forEach(item => {
  if (["A_B_B", "A_B_A"].includes(item.fieldGroup)) return;
  
  const val = Number(item.value || 0);
  const tgt = Number(unitTargetMap[item.fieldId]);
  const stat = unitStatusMap[item.fieldId];
  const selected = stat === "isLeading" ? "leading" : stat === "isLagging" ? "lagging" : "-";
  
  if (selected === "-" || isNaN(tgt)) return;
  
  themeCounts.Social.total++;
  if ((selected === "leading" && val >= tgt) || (selected === "lagging" && val < tgt)) {
    themeCounts.Social.reached++;
  }
});

// Gender diversity calculation (unchanged)
const femaleGroup = socResult.data.filter(item => item.fieldGroup === "A_B_B");
const maleGroup = socResult.data.filter(item => item.fieldGroup === "A_B_A");
if (femaleGroup.length > 0 && maleGroup.length > 0) {
  const femaleTotal = femaleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const maleTotal = maleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const totalEmployees = femaleTotal + maleTotal;
  const femalePercentage = totalEmployees > 0 ? ((femaleTotal / totalEmployees) * 100).toFixed(2) : "0.00";
  const femaleTargetReached = parseFloat(femalePercentage) >= 50;
  themeCounts.Social.total++;
  if (femaleTargetReached) themeCounts.Social.reached++;
}

//console.log(`Theme counts for ${unitName}:`, themeCounts);

console.log(`📊 ALL UNITS FINAL CALC - ${unitName}: Env=${themeCounts.Environment.reached}/${themeCounts.Environment.total} (${themeCounts.Environment.total > 0 ? ((themeCounts.Environment.reached / themeCounts.Environment.total) * 100).toFixed(2) : "0.00"}%), Gov=${themeCounts.Governance.reached}/${themeCounts.Governance.total}, Soc=${themeCounts.Social.reached}/${themeCounts.Social.total}`);

      summaryList.push({
        unitName,
        Environment: { total: themeCounts.Environment.total, reached: themeCounts.Environment.reached, percentage: themeCounts.Environment.total > 0 ? ((themeCounts.Environment.reached / themeCounts.Environment.total) * 100).toFixed(2) : "0.00" },
        Governance: { total: themeCounts.Governance.total, reached: themeCounts.Governance.reached, percentage: themeCounts.Governance.total > 0 ? ((themeCounts.Governance.reached / themeCounts.Governance.total) * 100).toFixed(2) : "0.00" },
        Social: { total: themeCounts.Social.total, reached: themeCounts.Social.reached, percentage: themeCounts.Social.total > 0 ? ((themeCounts.Social.reached / themeCounts.Social.total) * 100).toFixed(2) : "0.00" },
      });
    }

    setUnitWiseSummary(summaryList);
    setAllUnitsDetailedData(detailedDataList);

    // Calculate overall summary
    const totalThemeSummary = { Environment: { total: 0, reached: 0 }, Governance: { total: 0, reached: 0 }, Social: { total: 0, reached: 0 } };
    summaryList.forEach(unit => {
      ["Environment", "Governance", "Social"].forEach(theme => {
        totalThemeSummary[theme].total += unit[theme].total;
        totalThemeSummary[theme].reached += unit[theme].reached;
      });
    });

    const totalOverall = { total: totalThemeSummary.Environment.total + totalThemeSummary.Governance.total + totalThemeSummary.Social.total, reached: totalThemeSummary.Environment.reached + totalThemeSummary.Governance.reached + totalThemeSummary.Social.reached };
    const themePercentages = {
      Environment: totalThemeSummary.Environment.total > 0 ? ((totalThemeSummary.Environment.reached / totalThemeSummary.Environment.total) * 100).toFixed(2) : "0.00",
      Governance: totalThemeSummary.Governance.total > 0 ? ((totalThemeSummary.Governance.reached / totalThemeSummary.Governance.total) * 100).toFixed(2) : "0.00",
      Social: totalThemeSummary.Social.total > 0 ? ((totalThemeSummary.Social.reached / totalThemeSummary.Social.total) * 100).toFixed(2) : "0.00",
      Overall: totalOverall.total > 0 ? ((totalOverall.reached / totalOverall.total) * 100).toFixed(2) : "0.00",
    };

    setOverallThemeSummary({ ...totalThemeSummary, Overall: totalOverall, percentages: themePercentages });

    const updatedSummary = summaryList.map(unit => {
      const total = unit.Environment.total + unit.Governance.total + unit.Social.total;
      const reached = unit.Environment.reached + unit.Governance.reached + unit.Social.reached;
      const percentage = total > 0 ? ((reached / total) * 100).toFixed(2) : "0.00";
      return { ...unit, overallPercentage: percentage };
    });

    setUnitWiseSummary(updatedSummary);
  } catch (err) {
    console.error("❌ Error fetching unit data", err);
  } finally {
    setLoading(false);
  }
};


const generateRowsForUnit = (unitData, unitName = null) => {
  let srNo = 1;
  const rows = [];

  const calculateUnitGroupedWaterTarget = (groupedTargetValues) => {
    const total = Object.values(groupedTargetValues.water).reduce((sum, target) => sum + (target || 0), 0);
    if (frequency === "Monthly") {
      return (total / 12).toFixed(2);
    } else if (frequency === "YTD") {
  const fromMonthIndex = months.indexOf(ytdFromMonth);
  const toMonthIndex = months.indexOf(ytdToMonth);
  const monthsInRange = toMonthIndex - fromMonthIndex + 1;
  return ((total / 12) * monthsInRange).toFixed(2);
    } else {
      return total.toFixed(2);
    }
  };

  const calculateUnitGroupedWasteTarget = (groupedTargetValues) => {
    const total = Object.values(groupedTargetValues.waste).reduce((sum, target) => sum + (target || 0), 0);
    if (frequency === "Monthly") {
      return (total / 12).toFixed(2);
    } else if (frequency === "YTD") {
  const fromMonthIndex = months.indexOf(ytdFromMonth);
  const toMonthIndex = months.indexOf(ytdToMonth);
  const monthsInRange = toMonthIndex - fromMonthIndex + 1;
  return ((total / 12) * monthsInRange).toFixed(2);
    } else {
      return total.toFixed(2);
    }
  };

  // Grouped Water Data
  // if (unitData.scorecardData && unitData.scorecardData.groupedWater) {
  //   if (frequency === "Yearly") {
  //     const waterTotal = Object.entries(unitData.scorecardData.groupedWater)
  //       .filter(([key]) => key.endsWith(`-${year}`))
  //       .reduce((sum, [, value]) => sum + Number(value || 0), 0);

  //     if (waterTotal !== 0) {
  //       const targetValue = parseFloat(calculateUnitGroupedWaterTarget(unitData.groupedTargetValues));
  //        const percentage = targetValue > 0 ? (((targetValue - waterTotal) / targetValue) * 100).toFixed(2) : "False";

  //       rows.push(
  //         <tr key={`${unitName}-water-group`}>
  //           <td>{srNo++}</td>
  //           {showAllUnits && <td>{unitName}</td>}
  //           <td>Environment</td>
  //           <td>Water withdrawal by Source</td>
  //           <td>Absolute</td>
  //           <td>Cubic Meter</td>
  //           <td>{frequency}</td>
  //           <td>{frequency === "Monthly" ? month : frequency === "YTD" ? "YTD" : '-'}</td>
  //           <td>{year}</td>
  //           <td>{targetValue}</td>
  //           <td>{waterTotal.toFixed(2)}</td>
  //           <td style={{ color: waterTotal <= targetValue ? 'green' : 'red' }}>
  //             {percentage}%
  //           </td>
  //         </tr>
  //       );
  //     }
  //   }
  //    else if (frequency === "YTD") {
  //     // For YTD: Show one row with total values from ytdFromMonth to ytdToMonth
  //     const ytdWaterTotal = Object.entries(unitData.scorecardData.groupedWater)
  //       .filter(([key]) => {
  //         const [monthName, keyYear] = key.split('-');
  //         if (keyYear !== year) return false;
  //         return isWithinYTD(keyYear, monthName, year);
  //       })
  //       .reduce((sum, [, value]) => sum + Number(value || 0), 0);

  //     if (ytdWaterTotal !== 0) {
  //       const targetValue = parseFloat(calculateUnitGroupedWaterTarget(unitData.groupedTargetValues));

  //     const percentage = targetValue > 0 ? (((targetValue - ytdWaterTotal) / targetValue) * 100).toFixed(2) : "False";

  //       rows.push(
  //         <tr key={`${unitName}-water-group-ytd`}>
  //           <td>{srNo++}</td>
  //           {showAllUnits && <td>{unitName}</td>}
  //           <td>Environment</td>
  //           <td>Water withdrawal by Source</td>
  //           <td>Absolute</td>
  //           <td>Cubic Meter</td>
  //           <td>YTD</td>
  //           <td>{`YTD ${year} (${ytdFromMonth} to ${ytdToMonth})`}</td>
  //           <td>{year}</td>
  //           <td>{targetValue}</td>
  //           <td>{ytdWaterTotal.toFixed(2)}</td>
  //           <td style={{ color: ytdWaterTotal <= targetValue ? 'green' : 'red' }}>
  //             {percentage}%
  //           </td>
  //         </tr>
  //       );
  //     }
  //   } 
  //   else {
  //     // Monthly frequency logic
  //     const waterTotal = Object.entries(unitData.scorecardData.groupedWater)
  //       .filter(([key]) =>
  //         month === "All"
  //           ? key.includes(`-${year}`)
  //           : key.startsWith(`${month}-${year}`)
  //       )
  //       .reduce((sum, [, value]) => sum + Number(value || 0), 0);

  //     if (waterTotal !== 0) {
  //       const targetValue = parseFloat(calculateUnitGroupedWaterTarget(unitData.groupedTargetValues));

  //     const percentage = targetValue > 0 ? (((targetValue - waterTotal) / targetValue) * 100).toFixed(2) : "False";
      
  //       rows.push(
  //         <tr key={`${unitName}-water-group`}>
  //           <td>{srNo++}</td>
  //           {showAllUnits && <td>{unitName}</td>}
  //           <td>Environment</td>
  //           <td>Water withdrawal by Source</td>
  //           <td>Absolute</td>
  //           <td>Cubic Meter</td>
  //           <td>{frequency}</td>
  //           <td>{frequency === "Monthly" ? month : frequency === "YTD" ? "YTD" : '-'}</td>
  //           <td>{year}</td>
  //           <td>{targetValue}</td>
  //           <td>{waterTotal.toFixed(2)}</td>
  //           <td style={{ color: waterTotal <= targetValue ? 'green' : 'red' }}>
  //             {percentage}%
  //           </td>
  //         </tr>
  //       );
  //     }
  //   }
  // }


  // Grouped Water Data
if (unitData.scorecardData && (unitData.scorecardData.groupedWater || unitData.scorecardData.water)) {
  if (frequency === "Yearly") {
    let waterTotal = 0;
    
    // First try to get data from pre-aggregated groupedWater
    if (unitData.scorecardData.groupedWater) {
      waterTotal = Object.entries(unitData.scorecardData.groupedWater)
        .filter(([key]) => key.endsWith(`-${year}`))
        .reduce((sum, [, value]) => sum + Number(value || 0), 0);
    }
    
    // If no pre-aggregated data or waterTotal is 0, fall back to aggregating raw data
    if (waterTotal === 0 && Array.isArray(unitData.scorecardData.water)) {
      waterTotal = unitData.scorecardData.water
        .filter(item => {
          // Only include fieldGroup 'A' items (grouped water)
          if (item.fieldGroup !== 'A') return false;
          
          // Get year from either frequencyYear or frequencyDate
          let itemYear;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            itemYear = new Date(item.frequencyDate).getFullYear().toString();
          } else if (item.frequencyYear) {
            itemYear = item.frequencyYear;
          } else {
            return false;
          }
          
          return itemYear === year;
        })
        .reduce((sum, item) => sum + Number(item.prevValue || 0), 0);
    }

    if (waterTotal !== 0) {
      const targetValue = parseFloat(calculateUnitGroupedWaterTarget(unitData.groupedTargetValues));
      const percentage = targetValue > 0 ? (((targetValue - waterTotal) / targetValue) * 100).toFixed(2) : "False";

      rows.push(
        <tr key={`${unitName}-water-group`}>
          <td>{srNo++}</td>
          {showAllUnits && <td>{unitName}</td>}
          <td>Environment</td>
          <td>Water withdrawal by Source</td>
          <td>Absolute</td>
          <td>Cubic Meter</td>
          <td>{frequency}</td>
          <td>{frequency === "Monthly" ? month : frequency === "YTD" ? "YTD" : '-'}</td>
          <td>{year}</td>
          <td>{targetValue}</td>
          <td>{waterTotal.toFixed(2)}</td>
          <td style={{ color: waterTotal <= targetValue ? 'green' : 'red' }}>
            {percentage}%
          </td>
        </tr>
      );
    }
  } else if (frequency === "YTD") {
    let ytdWaterTotal = 0;
    
    // First try to get data from pre-aggregated groupedWater
    if (unitData.scorecardData.groupedWater) {
      ytdWaterTotal = Object.entries(unitData.scorecardData.groupedWater)
        .filter(([key]) => {
          const [monthName, keyYear] = key.split('-');
          if (keyYear !== year) return false;
          return isWithinYTD(keyYear, monthName, year);
        })
        .reduce((sum, [, value]) => sum + Number(value || 0), 0);
    }
    
    // If no pre-aggregated data or ytdWaterTotal is 0, fall back to aggregating raw data
    if (ytdWaterTotal === 0 && Array.isArray(unitData.scorecardData.water)) {
      ytdWaterTotal = unitData.scorecardData.water
        .filter(item => {
          // Only include fieldGroup 'A' items (grouped water)
          if (item.fieldGroup !== 'A') return false;
          
          let itemYear, itemMonth;
          
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            itemYear = date.getFullYear().toString();
            itemMonth = date.toLocaleString('default', { month: 'long' });
          } else if (item.frequencyType === "Monthly") {
            itemYear = item.frequencyYear;
            itemMonth = item.frequencyMonth;
          } else {
            return false;
          }
          
          if (itemYear !== year) return false;
          return isWithinYTD(itemYear, itemMonth, year);
        })
        .reduce((sum, item) => sum + Number(item.prevValue || 0), 0);
    }

    if (ytdWaterTotal !== 0) {
      const targetValue = parseFloat(calculateUnitGroupedWaterTarget(unitData.groupedTargetValues));
      const percentage = targetValue > 0 ? (((targetValue - ytdWaterTotal) / targetValue) * 100).toFixed(2) : "False";

      rows.push(
        <tr key={`${unitName}-water-group-ytd`}>
          <td>{srNo++}</td>
          {showAllUnits && <td>{unitName}</td>}
          <td>Environment</td>
          <td>Water withdrawal by Source</td>
          <td>Absolute</td>
          <td>Cubic Meter</td>
          <td>YTD</td>
          <td>{`YTD ${year} (${ytdFromMonth} to ${ytdToMonth})`}</td>
          <td>{year}</td>
          <td>{targetValue}</td>
          <td>{ytdWaterTotal.toFixed(2)}</td>
          <td style={{ color: ytdWaterTotal <= targetValue ? 'green' : 'red' }}>
            {percentage}%
          </td>
        </tr>
      );
    }
  } else {
    // Monthly frequency logic
    let waterTotal = 0;
    
    // First try to get data from pre-aggregated groupedWater
    if (unitData.scorecardData.groupedWater) {
      waterTotal = Object.entries(unitData.scorecardData.groupedWater)
        .filter(([key]) =>
          month === "All"
            ? key.includes(`-${year}`)
            : key.startsWith(`${month}-${year}`)
        )
        .reduce((sum, [, value]) => sum + Number(value || 0), 0);
    }
    
    // If no pre-aggregated data or waterTotal is 0, fall back to aggregating raw data
    if (waterTotal === 0 && Array.isArray(unitData.scorecardData.water)) {
      waterTotal = unitData.scorecardData.water
        .filter(item => {
          // Only include fieldGroup 'A' items (grouped water)
          if (item.fieldGroup !== 'A') return false;
          
          let itemYear, itemMonth;
          
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            itemYear = date.getFullYear().toString();
            itemMonth = date.toLocaleString('default', { month: 'long' });
          } else if (item.frequencyType === "Monthly") {
            itemYear = item.frequencyYear;
            itemMonth = item.frequencyMonth;
          } else {
            return false;
          }
          
          if (itemYear !== year) return false;
          if (month === "All") return true;
          return itemMonth === month;
        })
        .reduce((sum, item) => sum + Number(item.prevValue || 0), 0);
    }

    if (waterTotal !== 0) {
      const targetValue = parseFloat(calculateUnitGroupedWaterTarget(unitData.groupedTargetValues));
      const percentage = targetValue > 0 ? (((targetValue - waterTotal) / targetValue) * 100).toFixed(2) : "False";
      
      rows.push(
        <tr key={`${unitName}-water-group`}>
          <td>{srNo++}</td>
          {showAllUnits && <td>{unitName}</td>}
          <td>Environment</td>
          <td>Water withdrawal by Source</td>
          <td>Absolute</td>
          <td>Cubic Meter</td>
          <td>{frequency}</td>
          <td>{frequency === "Monthly" ? month : frequency === "YTD" ? "YTD" : '-'}</td>
          <td>{year}</td>
          <td>{targetValue}</td>
          <td>{waterTotal.toFixed(2)}</td>
          <td style={{ color: waterTotal <= targetValue ? 'green' : 'red' }}>
            {percentage}%
          </td>
        </tr>
      );
    }
  }
}

  // Main datasets (water, waste, emission, energy)
  const datasets = ['water', 'waste', 'emission', 'energy'];

  datasets.forEach(dataset => {
    if (Array.isArray(unitData.scorecardData[dataset])) {
      if (frequency === 'Yearly') {
        // For yearly: aggregate by fieldId for the selected year
        const yearlyAggregated = {};
        
        unitData.scorecardData[dataset]
          .filter(item => {
            // Skip grouped items
            if (dataset === 'water' && item.fieldGroup === 'A') return false;
            if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return false;
            
            // Get year from either frequencyYear or frequencyDate
            let itemYear;
            if (item.frequencyType === "Daily" && item.frequencyDate) {
              itemYear = new Date(item.frequencyDate).getFullYear().toString();
            } else if (item.frequencyYear) {
              itemYear = item.frequencyYear;
            } else {
              return false;
            }
            
            return itemYear === year;
          })
          .forEach((item) => {
            const fieldId = item.fieldId;
            
            if (!yearlyAggregated[fieldId]) {
              yearlyAggregated[fieldId] = { 
                ...item, 
                prevValue: 0,
                frequencyType: "Yearly",
                frequencyMonth: "-",
                frequencyYear: year
              };
            }
            yearlyAggregated[fieldId].prevValue += Number(item.prevValue || 0);
          });

        // Render aggregated yearly rows
        Object.values(yearlyAggregated).forEach((item) => {
          let fieldName = typeof item.fieldName === 'object' ? item.fieldName.name : item.fieldName || "";
          if (dataset === 'waste' && fieldName === 'Hazardous') {
            fieldName = 'Type of Waste Generated Hazardous';
          }
          // const unit = item.unitName || '-';
          const unit = dataset === 'energy' ? 'KWH' : (item.unitName || '-');
          const freq = "Yearly";
          const yr = year;
          const currentValue = item.prevValue || 0;

          const fieldType = fieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
          const target = unitData.targetValues[item.fieldId];
          const targetValue = target !== undefined && !isNaN(target) 
            ? Number(target).toFixed(2) : "-";

          const rawStatus = unitData.statusSelections[item.fieldId];
          const selectedStatus = rawStatus === "isLeading" ? "Leading" : 
                                rawStatus === "isLagging" ? "Lagging" : "-";

          const isTargetReached = parseFloat(targetValue) === 0 ? "False" :
            (selectedStatus === "Leading" && currentValue >= targetValue) ||
            (selectedStatus === "Lagging" && currentValue < targetValue) ? "True" : "False";

          const percentageDiff = targetValue !== "-" && parseFloat(targetValue) > 0
            ? selectedStatus === "Leading"
              ? (((currentValue - targetValue) / targetValue) * 100).toFixed(2)
              : (((targetValue - currentValue) / targetValue) * 100).toFixed(2)
            : "False";

          rows.push(
            <tr key={`${dataset}-${item.fieldId}-year-${unitName}`}>
              <td>{srNo++}</td>
              {showAllUnits && <td>{unitName}</td>}
              <td>Environment</td>
              <td>{fieldName}</td>
              <td>{fieldType}</td>
              <td>{unit}</td>
              <td>{freq}</td>
              <td>-</td>
              <td>{yr}</td>
              <td>{targetValue}</td>
              <td>{currentValue.toFixed(2)}</td>
              <td style={{ color: isTargetReached === "True" ? 'green' : 'red' }}>
                {percentageDiff}%
              </td>
            </tr>
          );
        });

      } 
      
      else if (frequency === 'YTD') {
        // For YTD: Show one row per field with total values from ytdFromMonth to ytdToMonth
        const ytdAggregated = {};
        
        unitData.scorecardData[dataset]
          .filter(item => {
            // Skip grouped items
            if (dataset === 'water' && item.fieldGroup === 'A') return false;
            if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return false;
            
            let itemYear, itemMonth;
            
            if (item.frequencyType === "Daily" && item.frequencyDate) {
              const date = new Date(item.frequencyDate);
              itemYear = date.getFullYear().toString();
              itemMonth = date.toLocaleString('default', { month: 'long' });
            } else if (item.frequencyType === "Monthly") {
              itemYear = item.frequencyYear;
              itemMonth = item.frequencyMonth;
            } else {
              return false;
            }
            
            if (itemYear !== year) return false;
            return isWithinYTD(itemYear, itemMonth, year);
          })
          .forEach((item) => {
            const fieldId = item.fieldId;
            
            if (!ytdAggregated[fieldId]) {
              ytdAggregated[fieldId] = {
                ...item,
                prevValue: 0,
                frequencyType: "YTD",
                frequencyMonth: `${ytdFromMonth} to ${ytdToMonth}`,
                frequencyYear: year
              };
            }
            
            ytdAggregated[fieldId].prevValue += Number(item.prevValue || 0);
          });

        // Render YTD rows - one per field with total values
        Object.values(ytdAggregated).forEach((item) => {
          let fieldName = typeof item.fieldName === 'object' ? item.fieldName.name : item.fieldName || "";
          if (dataset === 'waste' && fieldName === 'Hazardous') {
            fieldName = 'Type of Waste Generated Hazardous';
          }
          // const unit = item.unitName || '-';
          const unit = dataset === 'energy' ? 'KWH' : (item.unitName || '-');
          const freq = "YTD";
          const displayMonth = `YTD ${year} (${ytdFromMonth} to ${ytdToMonth})`;
          const displayYear = year;
          const currentValue = item.prevValue || 0;

          const fieldType = fieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
          const target = unitData.targetValues[item.fieldId];
          
          let targetValue;
          if (target !== undefined && !isNaN(target)) {
            // Calculate YTD target based on the number of months in range
            const fromMonthIndex = months.indexOf(ytdFromMonth);
            const toMonthIndex = months.indexOf(ytdToMonth);
            const monthsInRange = toMonthIndex - fromMonthIndex + 1;
            const monthlyTargetBase = Number(target) / 12;
            targetValue = (monthlyTargetBase * monthsInRange).toFixed(2);
          } else {
            targetValue = "-";
          }

          const rawStatus = unitData.statusSelections[item.fieldId];
          const selectedStatus = rawStatus === "isLeading" ? "Leading" : 
                                rawStatus === "isLagging" ? "Lagging" : "-";

          const isTargetReached = parseFloat(targetValue) === 0 ? "False" :
            (selectedStatus === "Leading" && currentValue >= targetValue) ||
            (selectedStatus === "Lagging" && currentValue < targetValue) ? "True" : "False";

          const percentageDiff = targetValue !== "-" && parseFloat(targetValue) > 0
            ? selectedStatus === "Leading"
              ? (((currentValue - targetValue) / targetValue) * 100).toFixed(2)
              : (((targetValue - currentValue) / targetValue) * 100).toFixed(2)
            : "False";

          rows.push(
            <tr key={`${dataset}-${item.fieldId}-ytd-${unitName}`}>
              <td>{srNo++}</td>
              {showAllUnits && <td>{unitName}</td>}
              <td>Environment</td>
              <td>{fieldName}</td>
              <td>{fieldType}</td>
              <td>{unit}</td>
              <td>{freq}</td>
              <td>{displayMonth}</td>
              <td>{displayYear}</td>
              <td>{targetValue}</td>
              <td>{currentValue.toFixed(2)}</td>
              <td style={{ color: isTargetReached === "True" ? 'green' : 'red' }}>
                {percentageDiff}%
              </td>
            </tr>
          );
        });

      }

      else {
  // For monthly: aggregate daily + monthly data by field and month
  const monthlyAggregated = {};
  
  let filteredData = unitData.scorecardData[dataset].filter(item => {
    // Skip grouped items
    if (dataset === 'water' && item.fieldGroup === 'A') return false;
    if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return false;
    
    let itemYear, itemMonth;
    
    // Handle daily data
    if (item.frequencyType === "Daily" && item.frequencyDate) {
      const date = new Date(item.frequencyDate);
      itemYear = date.getFullYear().toString();
      itemMonth = date.toLocaleString('default', { month: 'long' });
    } 
    // Handle monthly data
    else if (item.frequencyType === "Monthly") {
      itemYear = item.frequencyYear;
      itemMonth = item.frequencyMonth;
    } else {
      return false;
    }
    
    if (itemYear !== year) return false;
    if (month === "All") return true;
    return itemMonth === month;
  });

  // Group by fieldId and month, aggregating daily + monthly values
  filteredData.forEach((item) => {
    let itemMonth;
    if (item.frequencyType === "Daily" && item.frequencyDate) {
      const date = new Date(item.frequencyDate);
      itemMonth = date.toLocaleString('default', { month: 'long' });
    } else {
      itemMonth = item.frequencyMonth || '-';
    }
    
    const key = `${item.fieldId}-${itemMonth}`;
    
    if (!monthlyAggregated[key]) {
      monthlyAggregated[key] = {
        ...item,
        prevValue: 0,
        frequencyType: "Monthly", // Always show as Monthly
        frequencyMonth: itemMonth,
        frequencyYear: item.frequencyYear || new Date(item.frequencyDate).getFullYear().toString(),
        aggregatedFrom: [] // Track what was aggregated
      };
    }
    
    monthlyAggregated[key].prevValue += Number(item.prevValue || 0);
    monthlyAggregated[key].aggregatedFrom.push({
      type: item.frequencyType,
      value: item.prevValue,
      date: item.frequencyDate || `${item.frequencyMonth}-${item.frequencyYear}`
    });
  });

  // Render aggregated monthly rows
  Object.values(monthlyAggregated).forEach((item) => {
    let fieldName = typeof item.fieldName === 'object' ? item.fieldName.name : item.fieldName || "";
    if (dataset === 'waste' && fieldName === 'Hazardous') {
      fieldName = 'Type of Waste Generated Hazardous';
    }
    // const unit = item.unitName || '-';
    const unit = dataset === 'energy' ? 'KWH' : (item.unitName || '-');
    const currentValue = item.prevValue || 0;
    const displayMonth = item.frequencyMonth;
    const displayYear = item.frequencyYear;

    const fieldType = fieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
    const target = unitData.targetValues[item.fieldId];
    
    let targetValue;
    if (target !== undefined && !isNaN(target)) {
      targetValue = (Number(target) / 12).toFixed(2); // Monthly target
    } else {
      targetValue = "-";
    }

    const rawStatus = unitData.statusSelections[item.fieldId];
    const selectedStatus = rawStatus === "isLeading" ? "Leading" : 
                          rawStatus === "isLagging" ? "Lagging" : "-";

    const isTargetReached = parseFloat(targetValue) === 0 ? "False" :
      (selectedStatus === "Leading" && currentValue >= targetValue) ||
      (selectedStatus === "Lagging" && currentValue < targetValue) ? "True" : "False";

    const percentageDiff = targetValue !== "-" && parseFloat(targetValue) > 0
      ? selectedStatus === "Leading"
        ? (((currentValue - targetValue) / targetValue) * 100).toFixed(2)
        : (((targetValue - currentValue) / targetValue) * 100).toFixed(2)
      : "False";

 //   console.log(`Unit ${unitName} - Aggregated ${fieldName} for ${displayMonth}: ${currentValue} (from ${item.aggregatedFrom.length} entries)`);

    rows.push(
      <tr key={`${dataset}-${item.fieldId}-${displayMonth}-${unitName}-aggregated`}>
        <td>{srNo++}</td>
        {showAllUnits && <td>{unitName}</td>}
        <td>Environment</td>
        <td>{fieldName}</td>
        <td>{fieldType}</td>
        <td>{unit}</td>
        <td>Monthly</td>
        <td>{displayMonth}</td>
        <td>{displayYear}</td>
        <td>{targetValue}</td>
        <td>{currentValue.toFixed(2)}</td>
        <td style={{ color: isTargetReached === "True" ? 'green' : 'red' }}>
          {percentageDiff}%
        </td>
      </tr>
    );
  });
}
      
      
    }
  });

  // --- Governance Data ---
  if (unitData.governanceData?.length > 0 && frequency !== "YTD") {
    unitData.governanceData.forEach((item) => {
      const fieldId = item.fieldId;
      const fieldName = item.fieldName?.name || "";
      const fieldType = fieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
      const unit = "-";
      const frequencyType = "Yearly";
      const currentValue = Number(item.value || 0);
      const targetValue = unitData.targetValues[fieldId] || "0";
      const rawStatus = unitData.statusSelections[fieldId];
      const selectedStatus = rawStatus === "isLeading" ? "leading" : rawStatus === "isLagging" ? "lagging" : "-";
      
      rows.push(
        <tr key={`governance-${fieldId}-${unitName}`}>
          <td>{srNo++}</td>
          {showAllUnits && <td>{unitName}</td>}
          <td>Governance</td>
          <td>{fieldName}</td>
          <td>{fieldType}</td>
          <td>No's</td>
          <td>{frequencyType}</td>
          <td>-</td>
          <td>{item.year}</td>
          <td>{targetValue === "N/A" ? "-" : targetValue === 0 ? 0 : targetValue}</td>
          <td>{currentValue}</td>
          <td style={{ color: (() => {
            const tVal = targetValue === "-" || targetValue === undefined ? 0 : parseFloat(targetValue);
            if (tVal === 0) return currentValue !== 0 ? 'red' : 'green';
            if (tVal > 0) {
              const diff = ((tVal - currentValue) / tVal) * 100;
              return diff < 0 ? 'red' : 'green';
            }
            return 'inherit';
          })() }}>
            {(() => {
              const tVal = targetValue === "-" || targetValue === undefined ? 0 : parseFloat(targetValue);
              if (tVal === 0) return currentValue !== 0 ? 'False' : '0';
              if (tVal > 0) {
                const diff = ((tVal - currentValue) / tVal) * 100;
                return `${diff.toFixed(2)}%`;
              }
              return "-";
            })()}
          </td>
        </tr>
      );
    });
  }

  // --- Social Data ---
  if (unitData.socialData?.length > 0 && frequency !== "YTD") {
    const femaleGroup = unitData.socialData.filter(item => item.fieldGroup === "A_B_B");
    const maleGroup = unitData.socialData.filter(item => item.fieldGroup === "A_B_A");
    const femaleTotal = femaleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const maleTotal = maleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const totalEmployees = femaleTotal + maleTotal;
    const femalePercentage = totalEmployees > 0 ? ((femaleTotal / totalEmployees) * 100).toFixed(2) : "0.00";
    const targetValue = unitData.targetValues[femaleGroup[0]?.fieldId] || 0;
    const isTargetReached = parseFloat(femalePercentage) >= targetValue;
    
    if (femaleGroup.length > 0) {
      rows.push(
        <tr key={`female-diversity-${unitName}`}>
          <td>{srNo++}</td>
          {showAllUnits && <td>{unitName}</td>}
          <td>Social</td>
          <td>Female Workforce Diversity</td>
          <td>Absolute</td>
          <td>%</td>
          <td>Yearly</td>
          <td>-</td>
          <td>{year}</td>
          <td>{targetValue === "N/A" ? "-" : targetValue === 0 ? 0 : targetValue}%</td>
          <td>{femalePercentage}%</td>
          <td style={{ color: isTargetReached ? 'green' : 'red' }}>
            {((parseFloat(femalePercentage) - targetValue).toFixed(2))}%
          </td>
        </tr>
      );
    }

    unitData.socialData
      .filter(item => !["A_B_B", "A_B_A"].includes(item.fieldGroup))
      .forEach((item) => {
        const fieldId = item.fieldId;
        const rawFieldName = item.fieldName?.name?.trim() || "";
        const fieldName = rawFieldName;
        const fieldType = rawFieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute";
        const unit = rawFieldName === "Average hours of training per employee" ? "Hours" : "No's";
        const frequencyType = item.months ? "Monthly" : "Yearly";
        const frequencyMonth = item.months || "-";
        const frequencyYear = item.year;
        const currentValue = Number(item.value || 0);
        const target = unitData.targetValues[fieldId];
        const targetValue = target !== undefined ? Number(target) : "-";

        rows.push(
          <tr key={`social-${fieldId}-${unitName}`}>
            <td>{srNo++}</td>
            {showAllUnits && <td>{unitName}</td>}
            <td>Social</td>
            <td>{fieldName}</td>
            <td>{fieldType}</td>
            <td>{unit}</td>
            <td>{frequencyType}</td>
            <td>{frequencyMonth}</td>
            <td>{frequencyYear}</td>
            <td>{targetValue === "N/A" ? "-" : targetValue === 0 ? 0 : targetValue}</td>
            <td>{currentValue}</td>
            {/* <td style={{ color: (() => {
            const tVal = targetValue === "-" || targetValue === undefined ? 0 : parseFloat(targetValue);
              if (tVal === 0) return currentValue !== 0 ? 'red' : 'green';
              if (tVal > 0) {
                const diff = ((tVal - currentValue) / tVal) * 100;
                return diff < 0 ? 'red' : 'green';
              }
              return 'inherit';
            })() }}>
              {(() => {
                const tVal = targetValue === "-" || targetValue === undefined ? 0 : parseFloat(targetValue);
                if (tVal === 0) return currentValue !== 0 ? 'False' : '0';
                if (tVal > 0) {
                  const diff = ((tVal - currentValue) / tVal) * 100;
                  return `${diff.toFixed(2)}%`;
                }
                return "-";
              })()}
            </td> */}

            <td style={{ color: (() => {
  const tVal = targetValue === "-" || targetValue === undefined ? 0 : parseFloat(targetValue);
  const rawStatus = unitData.statusSelections[fieldId];
  const selectedStatus = rawStatus === "isLeading" ? "leading" : rawStatus === "isLagging" ? "lagging" : "-";
  
  if (tVal === 0) return currentValue !== 0 ? 'red' : 'green';
  if (tVal > 0 && selectedStatus !== "-") {
    // Leading: positive when current >= target
    // Lagging: positive when current < target
    const isTargetReached = (selectedStatus === "leading" && currentValue >= tVal) || 
                           (selectedStatus === "lagging" && currentValue < tVal);
    return isTargetReached ? 'green' : 'red';
  }
  return 'inherit';
})() }}>
  {(() => {
    const tVal = targetValue === "-" || targetValue === undefined ? 0 : parseFloat(targetValue);
    const rawStatus = unitData.statusSelections[fieldId];
    const selectedStatus = rawStatus === "isLeading" ? "leading" : rawStatus === "isLagging" ? "lagging" : "-";
    
    if (tVal === 0) return currentValue !== 0 ? 'False' : '0';
    if (tVal > 0 && selectedStatus !== "-") {
      let diff;
      if (selectedStatus === "leading") {
        // Leading: (current - target) / target * 100
        diff = ((currentValue - tVal) / tVal) * 100;
      } else if (selectedStatus === "lagging") {
        // Lagging: (target - current) / target * 100
        diff = ((tVal - currentValue) / tVal) * 100;
      }
      return `${diff.toFixed(2)}%`;
    }
    return "-";
  })()}
</td>
          </tr>
        );
      });
  }

  return rows;
};

useEffect(() => {
  if (!user?.id || !token) return;

  if (showAllUnits) {
    fetchAllUnitData();
  }
}, [user?.id, token, year, frequency, month, showAllUnits]);

useEffect(() => {
  if (showAllUnits) return;

  const themeCounts = {
    Environment: { total: 0, reached: 0 },
    Governance: { total: 0, reached: 0 },
    Social: { total: 0, reached: 0 },
  };

  const getGroupedTotal = (groupedData, rawData, groupKey, year, frequency, month, ytdFromMonth, ytdToMonth, months, isWithinYTD) => {
    let total = 0;
    // Try explicit grouped data first
    if (groupedData) {
      total = Object.entries(groupedData)
        .filter(([key]) => {
          if (frequency === "Yearly") {
            return key.endsWith(year) || key.startsWith('Yearly-');
          } else if (frequency === "YTD") {
            const [monthName, keyYear] = key.split('-');
            if (keyYear !== year) return false;
            return isWithinYTD(keyYear, monthName, year);
          } else {
            return month === "All" ? key.includes(year) : key.startsWith(`${month}-${year}`);
          }
        })
        .reduce((sum, [, value]) => sum + Number(value || 0), 0);
    }
    // Fallback to raw data aggregation if groupedData is missing or total is 0
    if ((!groupedData || total === 0) && Array.isArray(rawData)) {
      total = rawData
        .filter(item => {
          if (item.fieldGroup !== groupKey) return false;
          let itemYear, itemMonth;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            itemYear = date.getFullYear().toString();
            itemMonth = date.toLocaleString('default', { month: 'long' });
          } else if (item.frequencyType === "Monthly") {
            itemYear = item.frequencyYear;
            itemMonth = item.frequencyMonth;
          } else if (item.frequencyType === "Yearly") {
            itemYear = item.frequencyYear;
            itemMonth = "All";
          } else {
            return false;
          }
          if (itemYear !== year) return false;
          if (frequency === "Monthly") {
            if (month === "All") return true;
            return itemMonth === month;
          } else if (frequency === "YTD") {
            return isWithinYTD(itemYear, itemMonth, year);
          }
          return true;
        })
        .reduce((sum, item) => sum + Number(item.prevValue || 0), 0);
    }
    return total;
  };

  const calculate = () => {
    // --- Grouped Water ---
    const waterTotal = getGroupedTotal(
      scorecardData.groupedWater,
      scorecardData.water,
      "A",
      year,
      frequency,
      month,
      ytdFromMonth,
      ytdToMonth,
      months,
      isWithinYTD
    );
    const totalGroupedWaterTarget = Object.values(groupedTargetValues.water || {}).reduce((sum, target) => sum + (target || 0), 0);
    let waterTarget = totalGroupedWaterTarget;
    if (frequency === "Monthly") {
      waterTarget = month === "All" ? totalGroupedWaterTarget : totalGroupedWaterTarget / 12;
    } else if (frequency === "YTD") {
      const fromMonthIndex = months.indexOf(ytdFromMonth);
      const toMonthIndex = months.indexOf(ytdToMonth);
      const monthsInRange = toMonthIndex - fromMonthIndex + 1;
      waterTarget = (totalGroupedWaterTarget / 12) * monthsInRange;
    }
    // Always count grouped water as a KPI for yearly, monthly, YTD if there is any grouped data or raw data for the year
    if (waterTotal !== 0 || (frequency === "Yearly" && totalGroupedWaterTarget > 0)) {
      themeCounts.Environment.total++;
      if (waterTotal <= waterTarget) themeCounts.Environment.reached++;
    }

    // --- Grouped Waste ---
    const wasteTotal = getGroupedTotal(
      scorecardData.groupedWaste,
      scorecardData.waste,
      ["B1", "B2", "B3"],
      year,
      frequency,
      month,
      ytdFromMonth,
      ytdToMonth,
      months,
      isWithinYTD
    );
    const totalGroupedWasteTarget = Object.values(groupedTargetValues.waste || {}).reduce((sum, target) => sum + (target || 0), 0);
    let wasteTarget = totalGroupedWasteTarget;
    if (frequency === "Monthly") {
      wasteTarget = month === "All" ? totalGroupedWasteTarget : totalGroupedWasteTarget / 12;
    } else if (frequency === "YTD") {
      const fromMonthIndex = months.indexOf(ytdFromMonth);
      const toMonthIndex = months.indexOf(ytdToMonth);
      const monthsInRange = toMonthIndex - fromMonthIndex + 1;
      wasteTarget = (totalGroupedWasteTarget / 12) * monthsInRange;
    }
    if (wasteTotal !== 0 || (frequency === "Yearly" && totalGroupedWasteTarget > 0)) {
      themeCounts.Environment.total++;
      if (wasteTotal >= wasteTarget) themeCounts.Environment.reached++;
    }

    // --- Individual Environment KPIs ---
    ['water', 'waste', 'emission', 'energy'].forEach((dataset) => {
      const items = scorecardData[dataset] || [];
      let filteredData;
      if (frequency === "Monthly") {
        if (month !== "All") {
          filteredData = items.filter(i => {
            if (dataset === 'water' && i.fieldGroup === 'A') return false;
            if (dataset === 'waste' && ["B1", "B2", "B3"].includes(i.fieldGroup)) return false;
            let itemYear, itemMonth;
            if (i.frequencyType === "Daily" && i.frequencyDate) {
              const date = new Date(i.frequencyDate);
              itemYear = date.getFullYear().toString();
              itemMonth = date.toLocaleString('default', { month: 'long' });
            } else if (i.frequencyType === "Monthly") {
              itemYear = i.frequencyYear;
              itemMonth = i.frequencyMonth;
            } else {
              return false;
            }
            return itemYear === year && itemMonth === month;
          });
        } else {
          const aggregatedByField = {};
          items.filter(item => {
            if (dataset === 'water' && item.fieldGroup === 'A') return false;
            if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return false;
            let itemYear;
            if (item.frequencyType === "Daily" && item.frequencyDate) {
              itemYear = new Date(item.frequencyDate).getFullYear().toString();
            } else if (item.frequencyYear) {
              itemYear = item.frequencyYear;
            } else {
              return false;
            }
            return itemYear === year;
          }).forEach(item => {
            const fieldId = item.fieldId;
            if (!aggregatedByField[fieldId]) {
              aggregatedByField[fieldId] = {
                ...item,
                prevValue: 0,
                aggregatedMonths: new Set()
              };
            }
            aggregatedByField[fieldId].prevValue += Number(item.prevValue || 0);
            let itemMonth;
            if (item.frequencyType === "Daily" && item.frequencyDate) {
              itemMonth = new Date(item.frequencyDate).toLocaleString('default', { month: 'long' });
            } else {
              itemMonth = item.frequencyMonth || 'Unknown';
            }
            aggregatedByField[fieldId].aggregatedMonths.add(itemMonth);
          });
          filteredData = Object.values(aggregatedByField);
        }
      } else if (frequency === "YTD") {
        const ytdAggregatedByField = {};
        items.filter(item => {
          if (dataset === 'water' && item.fieldGroup === 'A') return false;
          if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return false;
          let itemYear, itemMonth;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            itemYear = date.getFullYear().toString();
            itemMonth = date.toLocaleString('default', { month: 'long' });
          } else if (item.frequencyYear) {
            itemYear = item.frequencyYear;
            itemMonth = item.frequencyMonth || 'Unknown';
          } else {
            return false;
          }
          if (itemYear !== year) return false;
          return isWithinYTD(itemYear, itemMonth, year);
        }).forEach(item => {
          const fieldId = item.fieldId;
          if (!ytdAggregatedByField[fieldId]) {
            ytdAggregatedByField[fieldId] = {
              ...item,
              prevValue: 0,
              aggregatedMonths: new Set()
            };
          }
          ytdAggregatedByField[fieldId].prevValue += Number(item.prevValue || 0);
          let itemMonth;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            itemMonth = new Date(item.frequencyDate).toLocaleString('default', { month: 'long' });
          } else {
            itemMonth = item.frequencyMonth || 'Unknown';
          }
          ytdAggregatedByField[fieldId].aggregatedMonths.add(itemMonth);
        });
        filteredData = Object.values(ytdAggregatedByField);
      } else {
        const yearlyAggregated = {};
        items.filter((item) => {
          if (dataset === 'water' && item.fieldGroup === 'A') return false;
          if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return false;
          let itemYear;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            itemYear = new Date(item.frequencyDate).getFullYear().toString();
          } else if (item.frequencyYear) {
            itemYear = item.frequencyYear;
          } else {
            return false;
          }
          return itemYear === year;
        }).forEach((item) => {
          const fieldId = item.fieldId;
          if (!yearlyAggregated[fieldId]) {
            yearlyAggregated[fieldId] = {
              ...item,
              prevValue: 0,
              frequencyType: "Yearly",
              frequencyMonth: "-",
              frequencyYear: year
            };
          }
          yearlyAggregated[fieldId].prevValue += Number(item.prevValue || 0);
        });
        filteredData = Object.values(yearlyAggregated);
      }
      const processedFields = new Set();
      filteredData.forEach((item) => {
        const fieldId = item.fieldId;
        if (processedFields.has(fieldId)) return;
        processedFields.add(fieldId);
        const rawStatus = statusSelections[fieldId];
        const selectedStatus = rawStatus === "isLeading" ? "leading"
          : rawStatus === "isLagging" ? "lagging" : "-";
        const target = targetValues[fieldId];
        if (target === undefined || selectedStatus === "-") return;
        let targetValue;
        if (frequency === "Yearly") {
          targetValue = target;
        } else if (frequency === "YTD") {
          const fromMonthIndex = months.indexOf(ytdFromMonth);
          const toMonthIndex = months.indexOf(ytdToMonth);
          const monthsInRange = toMonthIndex - fromMonthIndex + 1;
          targetValue = (target / 12) * monthsInRange;
        } else {
          targetValue = month === "All" ? target : target / 12;
        }
        const current = item.prevValue || 0;
        const isTargetReached = (selectedStatus === "leading" && current >= targetValue) ||
          (selectedStatus === "lagging" && current < targetValue);
        themeCounts.Environment.total++;
        if (isTargetReached) themeCounts.Environment.reached++;
      });
    });

    // --- Governance ---
    governanceData.forEach((item) => {
      const fieldId = item.fieldId;
      const val = Number(item.value || 0);
      const tgt = Number(targetValues[fieldId]);
      const stat = statusSelections[fieldId];
      const selected = stat === "isLeading" ? "leading" : stat === "isLagging" ? "lagging" : "-";
      if (selected === "-" || isNaN(tgt)) return;
      themeCounts.Governance.total++;
      if ((selected === "leading" && val >= tgt) || (selected === "lagging" && val < tgt)) {
        themeCounts.Governance.reached++;
      }
    });

    // --- Social ---
    const femaleGroup = socialData.filter(item => item.fieldGroup === "A_B_B");
    const maleGroup = socialData.filter(item => item.fieldGroup === "A_B_A");
    const femaleTotal = femaleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const maleTotal = maleGroup.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const totalEmployees = femaleTotal + maleTotal;
    const femalePercentage = totalEmployees > 0 ? (femaleTotal / totalEmployees) * 100 : 0;
    const targetValue = 50;
    if (femaleGroup.length > 0) {
      themeCounts.Social.total++;
      if (femalePercentage >= targetValue) themeCounts.Social.reached++;
    }
    socialData
      .filter(item => !["A_B_B", "A_B_A"].includes(item.fieldGroup))
      .forEach((item) => {
        const val = Number(item.value || 0);
        const tgt = Number(targetValues[item.fieldId]);
        const stat = statusSelections[item.fieldId];
        const selected = stat === "isLeading" ? "leading" : stat === "isLagging" ? "lagging" : "-";
        if (selected === "-" || isNaN(tgt)) return;
        themeCounts.Social.total++;
        if ((selected === "leading" && val >= tgt) || (selected === "lagging" && val < tgt)) {
          themeCounts.Social.reached++;
        }
      });

    setThemeCountsState(themeCounts);
    dashboardStore.setThemeSummaryByYear(year, {
      Environment: themeCounts.Environment,
      Governance: themeCounts.Governance,
      Social: themeCounts.Social,
    });
  };

  calculate();
}, [scorecardData, governanceData, socialData, frequency, month, year, showAllUnits, ytdFromMonth, ytdToMonth]);



useEffect(() => {
  fetchAssignUnits();
}, []);



const buildKPIChartData = (
  frequency,
  year, 
  month,
  scorecardData,
  targetValues,
  groupedTargetValues,
  governanceData,
  socialData,
  ytdFromMonth = null,
  ytdToMonth = null,
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  isWithinYTD = null
) => {
  console.log('🚀 [CHART DATA] Function called with parameters:', {
    frequency,
    year,
    month,
    scorecardDataKeys: Object.keys(scorecardData || {}),
    targetValuesKeys: Object.keys(targetValues || {}),
    groupedTargetValues,
    governanceDataLength: governanceData?.length,
    socialDataLength: socialData?.length,
    ytdFromMonth,
    ytdToMonth
  });

  const result = [];

  // Input validation
  if (!frequency || !year || !scorecardData || !targetValues) {
    console.error('❌ [CHART DATA] Missing required parameters:', {
      frequency: !!frequency,
      year: !!year,
      scorecardData: !!scorecardData,
      targetValues: !!targetValues
    });
    return result;
  }

  console.log('✅ [CHART DATA] Input validation passed');

  const themeLookup = {
    water: 'Environment',
    waste: 'Environment',
    emission: 'Environment',
    energy: 'Environment',
    governance: 'Governance',
    social: 'Social'
  };

  const extractDateInfo = (item) => {
    let itemYear = "", itemMonth = "";

    if (item.frequencyType === "Daily" && item.frequencyDate) {
      const date = new Date(item.frequencyDate);
      itemYear = date.getFullYear().toString();
      itemMonth = date.toLocaleString('default', { month: 'long' });
    } else if (item.frequencyType === "Monthly") {
      itemYear = item.frequencyYear?.toString();
      itemMonth = item.frequencyMonth;
    } else if (item.frequencyType === "Yearly") {
      itemYear = item.frequencyYear?.toString();
      itemMonth = "All";
    }

    return {
      year: itemYear,
      month: itemMonth?.toLowerCase()
    };
  };

  const isValidEnvItem = (item) => {
    const { year: itemYear, month: itemMonth } = extractDateInfo(item);
    
    console.log('🔍 [VALIDATION] Checking item validity:', {
      fieldName: typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName,
      itemYear,
      selectedYear: year.toString(),
      itemMonth,
      selectedMonth: month,
      frequency,
      frequencyType: item.frequencyType,
      valid: itemYear === year.toString()
    });
    
    if (!itemYear || itemYear !== year.toString()) return false;
    
    if (frequency === "Monthly" && month !== "All" && itemMonth !== month.toLowerCase()) return false;
    
    if (frequency === "YTD" && isWithinYTD) {
      const monthName = itemMonth.charAt(0).toUpperCase() + itemMonth.slice(1);
      return isWithinYTD(itemYear, monthName, year.toString());
    }
    
    return true;
  };

  // 1. Grouped Water Total Processing
  console.log('💧 [GROUPED WATER] Starting grouped water processing...');
  let waterTotal = 0;

  // First try pre-aggregated groupedWater
  if (scorecardData.groupedWater) {
    console.log('💧 [GROUPED WATER] Found pre-aggregated groupedWater:', scorecardData.groupedWater);
    
    const filteredEntries = Object.entries(scorecardData.groupedWater)
      .filter(([key]) => {
        let shouldInclude = false;
        if (frequency === "Yearly") {
          shouldInclude = key.endsWith(year.toString());
        } else if (frequency === "YTD" && isWithinYTD) {
          const [monthName, keyYear] = key.split('-');
          if (keyYear !== year.toString()) return false;
          shouldInclude = isWithinYTD(keyYear, monthName, year.toString());
        } else {
          shouldInclude = month === "All" ? key.includes(year.toString()) : key.startsWith(`${month}-${year}`);
        }
        
        console.log('💧 [GROUPED WATER] Filter check:', { key, shouldInclude, frequency, year, month });
        return shouldInclude;
      });
    
    console.log('💧 [GROUPED WATER] Filtered entries:', filteredEntries);
    
    waterTotal = filteredEntries.reduce((sum, [, val]) => sum + Number(val || 0), 0);
    console.log('💧 [GROUPED WATER] Pre-aggregated waterTotal:', waterTotal);
  } else {
    console.log('💧 [GROUPED WATER] No pre-aggregated groupedWater found');
  }

  // Fallback to raw data aggregation
  if (waterTotal === 0 && Array.isArray(scorecardData.water)) {
    console.log('💧 [GROUPED WATER] Fallback: Processing raw water data...');
    console.log('💧 [GROUPED WATER] Raw water data length:', scorecardData.water.length);
    
    const groupAItems = scorecardData.water.filter(item => item.fieldGroup === 'A');
    console.log('💧 [GROUPED WATER] Group A items found:', groupAItems.length);
    console.log('💧 [GROUPED WATER] Group A items:', groupAItems);
    
    waterTotal = scorecardData.water
      .filter(item => {
        if (item.fieldGroup !== 'A') return false;
        
        let itemYear, itemMonth;
        
        if (item.frequencyType === "Daily" && item.frequencyDate) {
          const date = new Date(item.frequencyDate);
          itemYear = date.getFullYear().toString();
          itemMonth = date.toLocaleString('default', { month: 'long' });
        } else if (item.frequencyType === "Monthly") {
          itemYear = item.frequencyYear?.toString();
          itemMonth = item.frequencyMonth;
        } else if (item.frequencyType === "Yearly") {
          itemYear = item.frequencyYear?.toString();
          itemMonth = "All";
        } else {
          console.log('💧 [GROUPED WATER] Invalid frequency type:', item.frequencyType);
          return false;
        }
        
        const yearMatch = itemYear === year.toString();
        console.log('💧 [GROUPED WATER] Item filter check:', {
          fieldName: typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName,
          itemYear,
          targetYear: year.toString(),
          yearMatch,
          itemMonth,
          frequency,
          prevValue: item.prevValue
        });
        
        if (!yearMatch) return false;
        
        if (frequency === "Monthly") {
          if (month === "All") return true;
          return itemMonth === month;
        } else if (frequency === "YTD" && isWithinYTD) {
          return isWithinYTD(itemYear, itemMonth, year.toString());
        } else {
          return true; // Yearly - include all
        }
      })
      .reduce((sum, item) => {
        const value = Number(item.prevValue || 0);
        console.log('💧 [GROUPED WATER] Adding value:', value, 'from item:', typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName);
        return sum + value;
      }, 0);
    
    console.log('💧 [GROUPED WATER] Raw aggregated waterTotal:', waterTotal);
  } else if (waterTotal === 0) {
    console.log('💧 [GROUPED WATER] No raw water data available');
  }

  // Calculate grouped target
  let groupedTarget = 0;
  if (groupedTargetValues && groupedTargetValues.water && groupedTargetValues.water.total) {
    if (frequency === "Monthly") {
      groupedTarget = groupedTargetValues.water.total / 12;
    } else if (frequency === "YTD" && ytdFromMonth && ytdToMonth) {
      const fromMonthIndex = months.indexOf(ytdFromMonth);
      const toMonthIndex = months.indexOf(ytdToMonth);
      const monthsInRange = toMonthIndex - fromMonthIndex + 1;
      groupedTarget = (groupedTargetValues.water.total / 12) * monthsInRange;
    } else {
      groupedTarget = groupedTargetValues.water.total;
    }
    console.log('💧 [GROUPED WATER] Calculated target:', groupedTarget);
  } else {
    console.log('💧 [GROUPED WATER] No grouped target values available');
  }

  if (waterTotal > 0 && groupedTarget > 0) {
    const waterEntry = {
      name: "Water Withdrawal Total (Environment)",
      current: +waterTotal.toFixed(2),
      target: +groupedTarget.toFixed(2),
      type: 'Environment',
    };
    result.push(waterEntry);
    console.log('💧 [GROUPED WATER] Added to result:', waterEntry);
  } else {
    console.log('💧 [GROUPED WATER] Not added to result:', { waterTotal, groupedTarget });
  }

  // 2. Process each theme
  console.log('🔄 [THEMES] Starting theme processing...');
  
  ['water', 'waste', 'emission', 'energy', 'governance', 'social'].forEach(theme => {
    console.log(`📊 [THEME-${theme.toUpperCase()}] Processing theme: ${theme}`);
    
    const records = theme === 'governance' ? (governanceData || [])
                  : theme === 'social' ? (socialData || [])
                  : (scorecardData[theme] || []);

    console.log(`📊 [THEME-${theme.toUpperCase()}] Records found:`, records.length);
    
    if (!Array.isArray(records)) {
      console.log(`📊 [THEME-${theme.toUpperCase()}] Records is not an array`);
      return;
    }

    const grouped = {};

    if (frequency === 'Yearly') {
      console.log(`📊 [THEME-${theme.toUpperCase()}] Processing for Yearly frequency`);
      
      const yearlyAggregated = {};
      
      const filteredRecords = records.filter(item => {
        // Skip grouped water (already handled above)
        if (theme === 'water' && item.fieldGroup === 'A') {
          console.log(`📊 [THEME-${theme.toUpperCase()}] Skipping grouped water item`);
          return false;
        }
        
        // Skip grouped waste
        if (theme === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) {
          console.log(`📊 [THEME-${theme.toUpperCase()}] Skipping grouped waste item`);
          return false;
        }
        
        // For governance and social, include all items
        if (theme === 'governance' || theme === 'social') {
          console.log(`📊 [THEME-${theme.toUpperCase()}] Including governance/social item:`, typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName);
          return true;
        }
        
        // For environment themes, check year
        let itemYear;
        if (item.frequencyType === "Daily" && item.frequencyDate) {
          itemYear = new Date(item.frequencyDate).getFullYear().toString();
        } else if (item.frequencyYear) {
          itemYear = item.frequencyYear?.toString();
        } else {
          console.log(`📊 [THEME-${theme.toUpperCase()}] No valid year found for item:`, item);
          return false;
        }
        
        const yearMatch = itemYear === year.toString();
        console.log(`📊 [THEME-${theme.toUpperCase()}] Year check:`, {
          fieldName: typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName,
          itemYear,
          targetYear: year.toString(),
          match: yearMatch,
          value: item.prevValue || item.value
        });
        
        return yearMatch;
      });
      
      console.log(`📊 [THEME-${theme.toUpperCase()}] Filtered records for yearly:`, filteredRecords.length);
      
      filteredRecords.forEach((item) => {
        const fieldId = item.fieldId;
        
        if (!yearlyAggregated[fieldId]) {
          yearlyAggregated[fieldId] = { 
            ...item, 
            prevValue: 0,
            value: 0
          };
        }
        yearlyAggregated[fieldId].prevValue += Number(item.prevValue || 0);
        yearlyAggregated[fieldId].value += Number(item.value || 0);
        
        console.log(`📊 [THEME-${theme.toUpperCase()}] Aggregating:`, {
          fieldId,
          fieldName: typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName,
          addingPrevValue: item.prevValue || 0,
          addingValue: item.value || 0,
          totalPrevValue: yearlyAggregated[fieldId].prevValue,
          totalValue: yearlyAggregated[fieldId].value
        });
      });

      console.log(`📊 [THEME-${theme.toUpperCase()}] Yearly aggregated:`, yearlyAggregated);

      // Convert yearly aggregated to grouped format
      Object.values(yearlyAggregated).forEach((item) => {
        const id = item.fieldId;
        const name = typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName;
        const value = Number(item.prevValue || item.value || 0);

        console.log(`📊 [THEME-${theme.toUpperCase()}] Processing aggregated item:`, { id, name, value });

        if (name && value > 0) {
          if (!grouped[id]) {
            grouped[id] = { name, current: 0 };
          }
          grouped[id].current += value;
          
          console.log(`📊 [THEME-${theme.toUpperCase()}] Added to grouped:`, { id, name, current: grouped[id].current });
        } else {
          console.log(`📊 [THEME-${theme.toUpperCase()}] Skipped (no name or zero value):`, { name, value });
        }
      });

    } else {
      console.log(`📊 [THEME-${theme.toUpperCase()}] Processing for ${frequency} frequency`);
      
      records.forEach(item => {
        const isEnv = ['water', 'waste', 'emission', 'energy'].includes(theme);
        const isValid = isEnv ? isValidEnvItem(item) : true;
        
        console.log(`📊 [THEME-${theme.toUpperCase()}] Item validation:`, {
          fieldName: typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName,
          isEnv,
          isValid,
          fieldGroup: item.fieldGroup
        });
        
        if (!isValid) return;

        // Skip grouped water
        if (theme === 'water' && item.fieldGroup === 'A') return;

        // Skip social group fields for % calc
        if (theme === 'social' && (item.fieldGroup === "A_B_A" || item.fieldGroup === "A_B_B")) return;

        const id = item.fieldId;
        const name = typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName;
        const value = Number(item.prevValue || item.value || 0);

        if (!grouped[id]) {
          grouped[id] = { name, current: 0 };
        }

        grouped[id].current += value;
        
        console.log(`📊 [THEME-${theme.toUpperCase()}] Added to grouped:`, { id, name, value, newTotal: grouped[id].current });
      });
    }

    console.log(`📊 [THEME-${theme.toUpperCase()}] Final grouped data:`, grouped);

    // Process grouped items
    Object.entries(grouped).forEach(([fieldId, { name, current }]) => {
      const rawTarget = parseFloat(targetValues[fieldId]);
      let displayName = name;
      if (theme === 'waste' && name === 'Hazardous') {
        displayName = 'Type of Waste Generated Hazardous';
      }
      
      console.log(`📊 [THEME-${theme.toUpperCase()}] Processing grouped item for result:`, {
        fieldId,
        displayName,
        current,
        rawTarget,
        isValidCurrent: !isNaN(current) && current > 0,
        isValidTarget: !isNaN(rawTarget)
      });
      
      if (!isNaN(current) && !isNaN(rawTarget) && current > 0) {
        let adjustedTarget = rawTarget;
        
        if (['water', 'waste', 'emission', 'energy'].includes(theme)) {
          if (frequency === "Monthly") {
            adjustedTarget = rawTarget / 12;
          } else if (frequency === "YTD" && ytdFromMonth && ytdToMonth) {
            const fromMonthIndex = months.indexOf(ytdFromMonth);
            const toMonthIndex = months.indexOf(ytdToMonth);
            const monthsInRange = toMonthIndex - fromMonthIndex + 1;
            adjustedTarget = (rawTarget / 12) * monthsInRange;
          }
        }

        const resultItem = {
          name: `${displayName} (${themeLookup[theme]})`,
          current: +current.toFixed(2),
          target: +adjustedTarget.toFixed(2),
          type: themeLookup[theme],
        };
        
        result.push(resultItem);
        console.log(`📊 [THEME-${theme.toUpperCase()}] Added to final result:`, resultItem);
      } else {
        console.log(`📊 [THEME-${theme.toUpperCase()}] Skipped for result:`, { displayName, current, rawTarget, reason: 'Invalid values or zero current' });
      }
    });
  });

  // 3. Female Workforce % (Social)
  if (frequency !== "YTD" && Array.isArray(socialData)) {
    console.log('👥 [FEMALE WORKFORCE] Processing female workforce percentage...');
    
    const femaleGroup = socialData.filter(i => i.fieldGroup === "A_B_B");
    const maleGroup = socialData.filter(i => i.fieldGroup === "A_B_A");

    console.log('👥 [FEMALE WORKFORCE] Groups found:', {
      femaleCount: femaleGroup.length,
      maleCount: maleGroup.length,
      femaleGroup,
      maleGroup
    });

    const femaleTotal = femaleGroup.reduce((sum, i) => sum + Number(i.value || 0), 0);
    const maleTotal = maleGroup.reduce((sum, i) => sum + Number(i.value || 0), 0);
    const total = femaleTotal + maleTotal;

    console.log('👥 [FEMALE WORKFORCE] Totals:', { femaleTotal, maleTotal, total });

    let femaleTarget = 50;
    if (femaleGroup.length > 0) {
      const femaleFieldId = femaleGroup[0].fieldId;
      if (targetValues && targetValues[femaleFieldId] !== undefined) {
        femaleTarget = Number(targetValues[femaleFieldId]);
      }
    }

    console.log('👥 [FEMALE WORKFORCE] Target:', femaleTarget);

    if (total > 0) {
      const percentFemale = ((femaleTotal / total) * 100).toFixed(2);
      const femaleItem = {
        name: "Female Workforce % (Social)",
        current: +percentFemale,
        target: +femaleTarget,
        type: 'Social',
      };
      result.push(femaleItem);
      console.log('👥 [FEMALE WORKFORCE] Added to result:', femaleItem);
    } else {
      console.log('👥 [FEMALE WORKFORCE] Not added - zero total');
    }
  } else {
    console.log('👥 [FEMALE WORKFORCE] Skipped - YTD frequency or no social data');
  }

  console.log('🎯 [FINAL RESULT] Chart data result:', result);
  console.log('🎯 [FINAL RESULT] Result length:', result.length);

  if (result.length === 0) {
    console.error('❌ [FINAL RESULT] No KPI data generated! Check the logs above for issues.');
  } else {
    console.log('✅ [FINAL RESULT] KPI data generated successfully!');
  }

  return result;
};

const buildYTDChartData = () => {
  const result = [];
  
  if (frequency !== "YTD") return result;
  
  const fromMonthIndex = months.indexOf(ytdFromMonth);
  const toMonthIndex = months.indexOf(ytdToMonth);
  const monthsInRange = toMonthIndex - fromMonthIndex + 1;
  
  // Process Environment datasets only
  ['water', 'waste', 'emission', 'energy'].forEach(dataset => {
    const items = scorecardData[dataset] || [];
    
    // Group by fieldId first
    const fieldGroups = {};
    
    items.forEach(item => {
      // Skip grouped items for individual field analysis
      if (dataset === 'water' && item.fieldGroup === 'A') return;
      if (dataset === 'waste' && ["B1", "B2", "B3"].includes(item.fieldGroup)) return;
      
      let itemYear, itemMonth;
      if (item.frequencyType === "Daily" && item.frequencyDate) {
        const date = new Date(item.frequencyDate);
        itemYear = date.getFullYear().toString();
        itemMonth = date.toLocaleString('default', { month: 'long' });
      } else if (item.frequencyYear) {
        itemYear = item.frequencyYear;
        itemMonth = item.frequencyMonth;
      } else {
        return;
      }
      
      // Only include data from selected year and YTD range
      if (itemYear !== year) return;
      if (!isWithinYTD(itemYear, itemMonth, year)) return;
      
      const fieldId = item.fieldId;
      const fieldName = typeof item.fieldName === 'object' ? item.fieldName?.name : item.fieldName || "";
      
      const unit = dataset === 'energy' ? 'KWH' : (item.unitName || '-');

      if (!fieldGroups[fieldId]) {
        fieldGroups[fieldId] = {
          fieldId,
          fieldName,
          unit: unit,
          fieldType: fieldName.toLowerCase().includes("intensity") ? "Intensity" : "Absolute",
          totalValue: 0
        };
      }
      
      // Aggregate total value for the entire YTD range
      fieldGroups[fieldId].totalValue += Number(item.prevValue || 0);
    });
    
    // Convert to chart format with TOTAL values and targets for the entire YTD range
    Object.values(fieldGroups).forEach(fieldGroup => {
      const { fieldId, fieldName, unit, fieldType, totalValue } = fieldGroup;
      // Update kpiName for waste/Hazardous
      let kpiName = fieldName;
      if (dataset === 'waste' && fieldName === 'Hazardous') {
        kpiName = 'Type of Waste Generated Hazardous';
      }
      // Get target for this field
      const yearlyTarget = targetValues[fieldId];
      if (!yearlyTarget || isNaN(yearlyTarget)) return;
      // Calculate YTD target: monthly target × number of months in range
      const monthlyTargetBase = yearlyTarget / 12;
      const ytdTarget = monthlyTargetBase * monthsInRange;
      if (totalValue !== 0) {
        result.push({
          kpiName: kpiName,
          period: `${ytdFromMonth} - ${ytdToMonth}`,
          currentValue: totalValue,
          targetValue: ytdTarget,
          unit: unit,
          fieldType: fieldType,
          dataset: dataset,
          fieldId: fieldId
        });
      }
    });
  });
  
  // Process Grouped Water Data separately (with total logic)
  if (scorecardData.groupedWater) {
    const ytdWaterTotal = Object.entries(scorecardData.groupedWater)
      .filter(([key]) => {
        const [monthName, keyYear] = key.split('-');
        if (keyYear !== year) return false;
        return isWithinYTD(keyYear, monthName, year);
      })
      .reduce((sum, [, value]) => sum + Number(value || 0), 0);

    if (ytdWaterTotal !== 0) {
      // Calculate YTD target for grouped water
      const totalGroupedWaterTarget = Object.values(groupedTargetValues.water).reduce((sum, target) => sum + (target || 0), 0);
      const monthlyTargetBase = totalGroupedWaterTarget / 12;
      const ytdTarget = monthlyTargetBase * monthsInRange;
      
      result.push({
        kpiName: "Water Withdrawal by Source (Grouped)",
        period: `${ytdFromMonth} - ${ytdToMonth}`,
        currentValue: ytdWaterTotal,
        targetValue: ytdTarget,
        unit: "Cubic Meter",
        fieldType: "Absolute",
        dataset: "water",
        fieldId: "grouped-water"
      });
    }
  }
  
  // Process Grouped Waste Data separately (with total logic)
  if (scorecardData.groupedWaste) {
    const ytdWasteTotal = Object.entries(scorecardData.groupedWaste)
      .filter(([key]) => {
        const [monthName, keyYear] = key.split('-');
        if (keyYear !== year) return false;
        return isWithinYTD(keyYear, monthName, year);
      })
      .reduce((sum, [, value]) => sum + Number(value || 0), 0);

    if (ytdWasteTotal !== 0) {
      // Calculate YTD target for grouped waste
      const totalGroupedWasteTarget = Object.values(groupedTargetValues.waste).reduce((sum, target) => sum + (target || 0), 0);
      const monthlyTargetBase = totalGroupedWasteTarget / 12;
      const ytdTarget = monthlyTargetBase * monthsInRange;
      
      result.push({
        kpiName: "Waste Generation (Grouped)",
        period: `${ytdFromMonth} - ${ytdToMonth}`,
        currentValue: ytdWasteTotal,
        targetValue: ytdTarget,
        unit: "Metric Tons",
        fieldType: "Absolute",
        dataset: "waste",
        fieldId: "grouped-waste"
      });
    }
  }
  
  console.log("YTD Chart Data:", result);
  return result;
};



  return (
    <Layout>
      <Card className="p-4">
        <Card.Body>


            <div className="d-flex flex-wrap gap-3 mb-4">
            <Form.Select value={frequency} onChange={(e) => {
              setFrequency(e.target.value);
              // Reset showAllUnits to false when switching to YTD
              if (e.target.value === "YTD") {
                setShowAllUnits(false);
              }
            }} style={{ width: '200px' }}>
              <option value="Yearly">Yearly</option>
              <option value="Monthly">Monthly</option>
              <option value="YTD">YTD (Year To Date)</option>
            </Form.Select>

            <Form.Select value={year} onChange={(e) => setYear(e.target.value)} style={{ width: '200px' }}>
              {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </Form.Select>

            {frequency === "Monthly" && (
              <Form.Select value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: '200px' }}>
                <option value="All">All Months</option>
                {months.map(mo => <option key={mo} value={mo}>{mo}</option>)}
              </Form.Select>
            )}

            {frequency === "YTD" && (
              <>
                <Form.Select value={ytdFromMonth} onChange={(e) => setYtdFromMonth(e.target.value)} style={{ width: '150px' }}>
                  <option disabled>From Month</option>
                  {months.map(mo => <option key={mo} value={mo}>{mo}</option>)}
                </Form.Select>
                <Form.Select value={ytdToMonth} onChange={(e) => setYtdToMonth(e.target.value)} style={{ width: '150px' }}>
                  <option disabled>To Month</option>
                  {months.map(mo => <option key={mo} value={mo}>{mo}</option>)}
                </Form.Select>
              </>
            )}
          </div>

        {frequency !== "YTD" && (
          <Form.Check 
            className="mt-4"
            type="checkbox" 
            label="Show All Company Units" 
            checked={showAllUnits} 
            onChange={(e) => setShowAllUnits(e.target.checked)} 
          />
        )}



          {showAllUnits ? (
  <>
    {frequency !== "YTD" && (
      <div id="all-units-chart">
        <ScorecardOverview
          unitData={unitWiseSummary}
          overallThemeSummary={overallThemeSummary}
          selectedPeriod={`${year}`}
        />
      </div>
    )}
    
    {frequency === "YTD" && (
      <div id="all-units-ytd-chart">
        <div className="alert alert-info mt-4">
          <h5>YTD Overview Chart</h5>
          <p>YTD chart for "Show All Units" is not yet implemented. Please uncheck "Show All Company Units" to see individual unit YTD charts.</p>
        </div>
      </div>
    )}
    
    <div id="all-units-table" style={{ display: 'none' }}>
      <div className="mt-4">
        <h5>Theme-wise Target Achievement Summary (All Units)</h5>
        <Table bordered>
          <thead>
            <tr>
              <th>Company Unit</th>
              <th>Theme</th>
              <th>Total KPIs</th>
              <th>Target Reached</th>
              <th>Achievement (%)</th>
              <th>Overall % (Per Unit)</th>
            </tr>
          </thead>
          <tbody>
            {unitWiseSummary.map(unit =>
              ["Environment", "Governance", "Social"].map((theme, index) => (
                <tr key={`${unit.unitName}-${theme}`}>
                  <td>{index === 0 ? unit.unitName : ""}</td>
                  <td>{theme}</td>
                  <td>{unit[theme].total}</td>
                  <td>{unit[theme].reached}</td>
                  <td style={{ color: parseFloat(unit[theme].percentage) < 0 ? 'red' : 'green' }}>{unit[theme].percentage}%</td>
                  <td style={{ color: parseFloat(unit.overallPercentage) < 0 ? 'red' : 'green' }}>{index === 0 ? `${unit.overallPercentage}%` : ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <Table bordered className="mt-4">
        <thead>
          <tr>
            <th colSpan="4" className="text-center bg-light">
              <h5 className="mb-0">Overall Theme Achievement Summary</h5>
            </th>
          </tr>
          <tr>
            <th>Theme</th>
            <th>Total KPIs</th>
            <th>Target Reached</th>
            <th>Achievement (%)</th>
          </tr>
        </thead>
        <tbody>
          {["Environment", "Governance", "Social", "Overall"].map((theme) => (
            <tr key={theme}>
              <td>{theme}</td>
              <td>{overallThemeSummary[theme]?.total}</td>
              <td>{overallThemeSummary[theme]?.reached}</td>
              <td style={{ color: parseFloat(overallThemeSummary.percentages[theme]) < 0 ? 'red' : 'green' }}>{overallThemeSummary.percentages[theme]}%</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>


   
    {frequency !== "YTD" && (
      <div className="mb-3">
        <button className="btn btn-primary" onClick={() => handleDownloadChartAndTableImage(showAllUnits, year)}>
          Download Chart & Table as Image
        </button>
      </div>
    )}
  </>
) : (
  <>
 {frequency !== "YTD" && (
      <div id="single-unit-chart">
        {assignUnitsRaw.length > 0 && (
          <ScorecardOverview
            unitData={[{
              unitName: getCompanyUnitName(),
              Environment: {
                ...themeCountsState.Environment,
                percentage: themeCountsState.Environment.total > 0
                  ? ((themeCountsState.Environment.reached / themeCountsState.Environment.total) * 100).toFixed(2)
                  : "0.00"
              },
              Governance: {
                ...themeCountsState.Governance,
                percentage: themeCountsState.Governance.total > 0
                  ? ((themeCountsState.Governance.reached / themeCountsState.Governance.total) * 100).toFixed(2)
                  : "0.00"
              },
              Social: {
                ...themeCountsState.Social,
                percentage: themeCountsState.Social.total > 0
                  ? ((themeCountsState.Social.reached / themeCountsState.Social.total) * 100).toFixed(2)
                  : "0.00"
              },
              overallPercentage: (() => {
                const total = Object.values(themeCountsState).reduce((sum, obj) => sum + obj.total, 0);
                const reached = Object.values(themeCountsState).reduce((sum, obj) => sum + obj.reached, 0);
                return total > 0 ? ((reached / total) * 100).toFixed(2) : "0.00";
              })()
            }]}
            overallThemeSummary={{
              Environment: themeCountsState.Environment,
              Governance: themeCountsState.Governance,
              Social: themeCountsState.Social,
              Overall: {
                total: Object.values(themeCountsState).reduce((sum, obj) => sum + obj.total, 0),
                reached: Object.values(themeCountsState).reduce((sum, obj) => sum + obj.reached, 0),
              },
              percentages: {
                Environment: themeCountsState.Environment.total > 0
                  ? ((themeCountsState.Environment.reached / themeCountsState.Environment.total) * 100).toFixed(2)
                  : "0.00",
                Governance: themeCountsState.Governance.total > 0
                  ? ((themeCountsState.Governance.reached / themeCountsState.Governance.total) * 100).toFixed(2)
                  : "0.00",
                Social: themeCountsState.Social.total > 0
                  ? ((themeCountsState.Social.reached / themeCountsState.Social.total) * 100).toFixed(2)
                  : "0.00",
                Overall: (() => {
                  const total = Object.values(themeCountsState).reduce((sum, obj) => sum + obj.total, 0);
                  const reached = Object.values(themeCountsState).reduce((sum, obj) => sum + obj.reached, 0);
                  return total > 0 ? ((reached / total) * 100).toFixed(2) : "0.00";
                })()
              }
            }}
            selectedPeriod={`${year}`}
          />
        )}
      </div>
    )}
       
    {frequency === "YTD" && (
      <div id="single-unit-ytd-chart">
        <YTDChart 
          ytdData={buildYTDChartData()} 
          selectedPeriod={`YTD ${year} (${ytdFromMonth} to ${ytdToMonth})`}
           year={year}
  fromMonth={ytdFromMonth}
  toMonth={ytdToMonth}
        />
      </div>
    )}
    
    <div id="single-unit-table" style={{ display: 'none' }}>
      <div className="mt-4">
        <h5>Theme-wise Target Achievement Summary (Selected Unit)</h5>
        <Table bordered>
          <thead>
            <tr>
              <th>Theme</th>
              <th>Total KPIs</th>
              <th>Target Reached</th>
              <th>Achievement (%)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(themeCountsState).map(([theme, { total, reached }]) => {
              const percentage = total > 0 ? ((reached / total) * 100).toFixed(2) : "0.00";
              return (
                <tr key={theme}>
                  <td>{theme}</td>
                  <td>{total}</td>
                  <td>{reached}</td>
                  <td style={{ color: parseFloat(percentage) < 0 ? 'red' : 'green' }}>{percentage}%</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
    {frequency !== "YTD" && (
      <div className="mb-3">
        <button className="btn btn-primary" onClick={() => handleDownloadChartAndTableImage(showAllUnits, year)}>
          Download Chart & Table as Image
        </button>
      </div>
    )}
  </>
)}


{frequency !== "YTD" && (
  <Card className="p-3 my-4">
    <h5 className="mb-3">KPI Performance vs Target</h5>
    <div style={{ height: 500 }}>
      <ScorecardKPIChart 
       // kpiData={buildKPIChartData()}
       kpiData={buildKPIChartData(
  frequency,
  year, 
  month,
  scorecardData,
  targetValues,
  groupedTargetValues,
  governanceData,
  socialData,
  ytdFromMonth,
  ytdToMonth,
  months,
  isWithinYTD
)} 
        year={year} 
        targetYear={String(Number(year) - 1)}
      />
    </div>
  </Card>
)}


          <h4 className="mt-4">Scorecard Data</h4>

          <div style={{ maxHeight: '500px', overflowY: 'auto', position: 'relative' }}>
       <Table id="scorecard-table">
        <thead 
        
        style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          zIndex: 10,
          width : '100%'
        }}>
        
      <tr>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>S. No</th>
        {showAllUnits && (
          <th
            style={{
              position: 'sticky',
              top: 0,
              backgroundColor: '#f8f9fa',
              boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
            }}
          >Company Unit</th>
        )}
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Theme</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Description of Target</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Target Type</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>UOM</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Frequency Type</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Frequency Month</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Frequency Year</th>
        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)' }}>{`Target Value based on Base Year Value (${Number(year)-1})`}</th>
        <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)' }}>{`Current Value (${year})`}</th>
        <th style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: '#f8f9fa', 
          boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.1)'
        }}>Percentage</th>
      </tr>

  </thead>

<tbody>
  {loading ? (
    <tr>
      <td colSpan={showAllUnits ? 12 : 11}>Loading data...</td>
    </tr>
  ) : showAllUnits ? (
    // For "Show All Units" - use generateRowsForUnit for each unit
    allUnitsDetailedData.length > 0 ? (
      allUnitsDetailedData.flatMap(unitData => 
        generateRowsForUnit(unitData, unitData.unitName)
      )
    ) : (
      <tr>
        <td colSpan={showAllUnits ? 12 : 11}>No unit data available</td>
      </tr>
    )
  ) : (() => {
    // For single unit view - use your existing logic but call generateRowsForUnit with current unit data
    const currentUnitData = {
      unitName: getCompanyUnitName(),
      unitId: companyUnit,
      scorecardData: {
        ...scorecardData,
        groupedWater: scorecardData.groupedWater || {},
        groupedWaste: scorecardData.groupedWaste || {},
      },
      governanceData: governanceData || [],
      socialData: socialData || [],
      targetValues: targetValues,
      statusSelections: statusSelections,
      groupedTargetValues: groupedTargetValues,
    };

    return generateRowsForUnit(currentUnitData, null);
  })()}
</tbody>

</Table>
</div>
<div className="mt-3 mb-3">
  <button className="btn btn-success"              onClick={() => handleDownloadExcel(year, frequency, month)}>
    Download
  </button>
</div>

        </Card.Body>
      </Card>
      
    </Layout>
  );
};

export default observer(ScorecardExcel);
